import { useReducer, useEffect, useRef, useCallback } from 'react'
import {
  apiAppealsDashboard,
  apiAppealsHistory,
  apiAppealDetail,
  apiUpdateOutcome,
  apiSubmitDenialFeedback,
  apiAppealsInsights,
  apiAppealsSearch,
  apiUpdateReferenceTag,
  apiGenerateP2PFromRecord,
  apiPayerDirectorySearch,
  apiGetDeadlines,
  apiUpdateDeadline,
  apiEscalateAppeal,
  apiAppealTimeline,
  apiAdvanceAppeal,
  apiAppealNextSteps,
  apiGetROIDashboard,
  apiUpdateAppealFinancials,
  apiGetMyDeadlines,
  apiExportAppeals,
  apiBulkUpdateAppeals,
  apiAssignAppeal,
  apiGetStaffWorkload,
  apiGetTeam,
  apiGetPracticeReport,
} from './api'
import type {
  AppealsDashboardData, AppealItem, AppealDetail, AppealsSearchParams,
  P2PData, PayerCarrierSummary,
  DeadlineItem, DeadlineSummary,
  AppealTimelineData, AppealStageWithStatus, AppealNextStepsData,
  AppealChecklistItem, AdvanceAppealResult, AppealTimelineEvent,
  ROIDashboardData,
  MyDeadlineItem, StaffWorkloadItem, TeamMember,
  PracticeReport,
} from './api'

// ── Outcome badge colors ────────────────────────────────────────────────────
const OUTCOME_STYLES: Record<string, string> = {
  pending: 'bg-slate-100 text-slate-600',
  submitted: 'bg-blue-100 text-blue-700',
  approved: 'bg-emerald-100 text-emerald-700',
  denied: 'bg-red-100 text-red-700',
}
const OUTCOME_LABELS: Record<string, string> = {
  pending: 'Not Submitted',
  submitted: 'Submitted',
  approved: 'Approved',
  denied: 'Denied',
}

// Import canonical CARRIERS list from shared types
import { CARRIERS } from './components/types'

const DATE_RANGES = [
  { label: 'All Time', value: '' },
  { label: 'Last 7 Days', value: '7' },
  { label: 'Last 30 Days', value: '30' },
  { label: 'Last 90 Days', value: '90' },
]

const SORT_OPTIONS = [
  { label: 'Newest First', sort_by: 'created_at', sort_order: 'desc' },
  { label: 'Oldest First', sort_by: 'created_at', sort_order: 'asc' },
  { label: 'Drug Name', sort_by: 'denied_product', sort_order: 'asc' },
  { label: 'Carrier', sort_by: 'carrier', sort_order: 'asc' },
]

const PLAN_PRICES: Record<string, number> = {
  starter: 149,
  professional: 299,
  professional_plus: 399,
  enterprise: 799,
}

interface Props {
  onClose: () => void
  plan?: string
}

// ── Default P2P expanded sections ─────────────────────────────────────────
const DEFAULT_P2P_SECTIONS: Record<string, boolean> = {
  opening: true, talking_points: true, objections: true, clinical: false, carrier_tips: false, closing: true, unsuccessful: false,
}

// ── UI State (tabs, modals, loading spinners, expanded sections) ──────────
type TabId = 'deadlines' | 'overview' | 'appeals' | 'insights' | 'roi' | 'report' | 'team'

interface UIState {
  activeTab: TabId
  loading: boolean
  appealsLoading: boolean
  detailLoading: boolean
  insightsLoading: boolean
  deadlinesLoading: boolean
  timelineLoading: boolean
  roiLoading: boolean
  reportLoading: boolean
  staffWorkloadLoading: boolean
  p2pLoading: boolean
  feedbackSubmitting: boolean
  savingTag: boolean
  savingFinancials: boolean
  bulkUpdating: boolean
  exporting: boolean
  advancingAppeal: boolean
  showFeedbackForm: boolean
  showP2pModal: boolean
  showAdvanceModal: boolean
  editingFinancials: boolean
  updatingOutcome: string | null
  deadlineEscalating: string | null
  deadlineSubmitting: string | null
  assigningAppeal: string | null
  p2pExpandedSections: Record<string, boolean>
  carrierContactToast: string | null
}

type UIAction =
  | { type: 'SET_ACTIVE_TAB'; tab: TabId }
  | { type: 'SET_LOADING'; value: boolean }
  | { type: 'SET_APPEALS_LOADING'; value: boolean }
  | { type: 'SET_DETAIL_LOADING'; value: boolean }
  | { type: 'SET_INSIGHTS_LOADING'; value: boolean }
  | { type: 'SET_DEADLINES_LOADING'; value: boolean }
  | { type: 'SET_TIMELINE_LOADING'; value: boolean }
  | { type: 'SET_ROI_LOADING'; value: boolean }
  | { type: 'SET_REPORT_LOADING'; value: boolean }
  | { type: 'SET_STAFF_WORKLOAD_LOADING'; value: boolean }
  | { type: 'SET_P2P_LOADING'; value: boolean }
  | { type: 'SET_FEEDBACK_SUBMITTING'; value: boolean }
  | { type: 'SET_SAVING_TAG'; value: boolean }
  | { type: 'SET_SAVING_FINANCIALS'; value: boolean }
  | { type: 'SET_BULK_UPDATING'; value: boolean }
  | { type: 'SET_EXPORTING'; value: boolean }
  | { type: 'SET_ADVANCING_APPEAL'; value: boolean }
  | { type: 'SET_SHOW_FEEDBACK_FORM'; value: boolean }
  | { type: 'SET_SHOW_P2P_MODAL'; value: boolean }
  | { type: 'SET_SHOW_ADVANCE_MODAL'; value: boolean }
  | { type: 'SET_EDITING_FINANCIALS'; value: boolean }
  | { type: 'SET_UPDATING_OUTCOME'; value: string | null }
  | { type: 'SET_DEADLINE_ESCALATING'; value: string | null }
  | { type: 'SET_DEADLINE_SUBMITTING'; value: string | null }
  | { type: 'SET_ASSIGNING_APPEAL'; value: string | null }
  | { type: 'SET_P2P_EXPANDED_SECTIONS'; value: Record<string, boolean> }
  | { type: 'TOGGLE_P2P_SECTION'; section: string }
  | { type: 'SET_CARRIER_CONTACT_TOAST'; value: string | null }

const uiInitialState: UIState = {
  activeTab: 'deadlines',
  loading: true,
  appealsLoading: false,
  detailLoading: false,
  insightsLoading: false,
  deadlinesLoading: false,
  timelineLoading: false,
  roiLoading: false,
  reportLoading: false,
  staffWorkloadLoading: false,
  p2pLoading: false,
  feedbackSubmitting: false,
  savingTag: false,
  savingFinancials: false,
  bulkUpdating: false,
  exporting: false,
  advancingAppeal: false,
  showFeedbackForm: false,
  showP2pModal: false,
  showAdvanceModal: false,
  editingFinancials: false,
  updatingOutcome: null,
  deadlineEscalating: null,
  deadlineSubmitting: null,
  assigningAppeal: null,
  p2pExpandedSections: { ...DEFAULT_P2P_SECTIONS },
  carrierContactToast: null,
}

function uiReducer(state: UIState, action: UIAction): UIState {
  switch (action.type) {
    case 'SET_ACTIVE_TAB': return { ...state, activeTab: action.tab }
    case 'SET_LOADING': return { ...state, loading: action.value }
    case 'SET_APPEALS_LOADING': return { ...state, appealsLoading: action.value }
    case 'SET_DETAIL_LOADING': return { ...state, detailLoading: action.value }
    case 'SET_INSIGHTS_LOADING': return { ...state, insightsLoading: action.value }
    case 'SET_DEADLINES_LOADING': return { ...state, deadlinesLoading: action.value }
    case 'SET_TIMELINE_LOADING': return { ...state, timelineLoading: action.value }
    case 'SET_ROI_LOADING': return { ...state, roiLoading: action.value }
    case 'SET_REPORT_LOADING': return { ...state, reportLoading: action.value }
    case 'SET_STAFF_WORKLOAD_LOADING': return { ...state, staffWorkloadLoading: action.value }
    case 'SET_P2P_LOADING': return { ...state, p2pLoading: action.value }
    case 'SET_FEEDBACK_SUBMITTING': return { ...state, feedbackSubmitting: action.value }
    case 'SET_SAVING_TAG': return { ...state, savingTag: action.value }
    case 'SET_SAVING_FINANCIALS': return { ...state, savingFinancials: action.value }
    case 'SET_BULK_UPDATING': return { ...state, bulkUpdating: action.value }
    case 'SET_EXPORTING': return { ...state, exporting: action.value }
    case 'SET_ADVANCING_APPEAL': return { ...state, advancingAppeal: action.value }
    case 'SET_SHOW_FEEDBACK_FORM': return { ...state, showFeedbackForm: action.value }
    case 'SET_SHOW_P2P_MODAL': return { ...state, showP2pModal: action.value }
    case 'SET_SHOW_ADVANCE_MODAL': return { ...state, showAdvanceModal: action.value }
    case 'SET_EDITING_FINANCIALS': return { ...state, editingFinancials: action.value }
    case 'SET_UPDATING_OUTCOME': return { ...state, updatingOutcome: action.value }
    case 'SET_DEADLINE_ESCALATING': return { ...state, deadlineEscalating: action.value }
    case 'SET_DEADLINE_SUBMITTING': return { ...state, deadlineSubmitting: action.value }
    case 'SET_ASSIGNING_APPEAL': return { ...state, assigningAppeal: action.value }
    case 'SET_P2P_EXPANDED_SECTIONS': return { ...state, p2pExpandedSections: action.value }
    case 'TOGGLE_P2P_SECTION': return { ...state, p2pExpandedSections: { ...state.p2pExpandedSections, [action.section]: !state.p2pExpandedSections[action.section] } }
    case 'SET_CARRIER_CONTACT_TOAST': return { ...state, carrierContactToast: action.value }
  }
}

// ── Data State (fetched data, selected items) ─────────────────────────────
interface DataState {
  dashboard: AppealsDashboardData | null
  appeals: AppealItem[]
  appealsTotal: number
  appealsPages: number
  selectedAppeal: AppealDetail | null
  insights: any[]
  deadlines: DeadlineItem[]
  deadlineSummary: DeadlineSummary | null
  carrierContact: PayerCarrierSummary | null
  timelineData: AppealTimelineData | null
  nextStepsData: AppealNextStepsData | null
  advanceResult: AdvanceAppealResult | null
  selectedTimelineEvent: AppealTimelineEvent | null
  p2pData: P2PData | null
  p2pText: string
  p2pHtml: string
  roiData: ROIDashboardData | null
  teamMembers: TeamMember[]
  isTeamOwner: boolean
  staffWorkload: StaffWorkloadItem[]
  myDeadlines: MyDeadlineItem[]
  practiceReport: PracticeReport | null
  selectedIds: Set<string>
}

type DataAction =
  | { type: 'SET_DASHBOARD'; value: AppealsDashboardData | null }
  | { type: 'SET_APPEALS'; appeals: AppealItem[]; total: number; pages: number }
  | { type: 'UPDATE_APPEALS'; updater: (prev: AppealItem[]) => AppealItem[] }
  | { type: 'SET_SELECTED_APPEAL'; value: AppealDetail | null }
  | { type: 'UPDATE_SELECTED_APPEAL'; updater: (prev: AppealDetail | null) => AppealDetail | null }
  | { type: 'SET_INSIGHTS'; value: any[] }
  | { type: 'SET_DEADLINES'; deadlines: DeadlineItem[]; summary: DeadlineSummary | null }
  | { type: 'SET_CARRIER_CONTACT'; value: PayerCarrierSummary | null }
  | { type: 'SET_TIMELINE_DATA'; value: AppealTimelineData | null }
  | { type: 'SET_NEXT_STEPS_DATA'; value: AppealNextStepsData | null }
  | { type: 'SET_ADVANCE_RESULT'; value: AdvanceAppealResult | null }
  | { type: 'SET_SELECTED_TIMELINE_EVENT'; value: AppealTimelineEvent | null }
  | { type: 'SET_P2P'; data: P2PData | null; text: string; html: string }
  | { type: 'RESET_P2P' }
  | { type: 'SET_ROI_DATA'; value: ROIDashboardData | null }
  | { type: 'SET_TEAM_INFO'; members: TeamMember[]; isOwner: boolean }
  | { type: 'SET_STAFF_WORKLOAD'; value: StaffWorkloadItem[] }
  | { type: 'SET_MY_DEADLINES'; value: MyDeadlineItem[] }
  | { type: 'SET_PRACTICE_REPORT'; value: PracticeReport | null }
  | { type: 'SET_SELECTED_IDS'; value: Set<string> }
  | { type: 'TOGGLE_SELECT_APPEAL'; id: string }
  | { type: 'TOGGLE_SELECT_ALL' }
  | { type: 'CLEAR_SELECTION' }

const dataInitialState: DataState = {
  dashboard: null,
  appeals: [],
  appealsTotal: 0,
  appealsPages: 1,
  selectedAppeal: null,
  insights: [],
  deadlines: [],
  deadlineSummary: null,
  carrierContact: null,
  timelineData: null,
  nextStepsData: null,
  advanceResult: null,
  selectedTimelineEvent: null,
  p2pData: null,
  p2pText: '',
  p2pHtml: '',
  roiData: null,
  teamMembers: [],
  isTeamOwner: false,
  staffWorkload: [],
  myDeadlines: [],
  practiceReport: null,
  selectedIds: new Set(),
}

function dataReducer(state: DataState, action: DataAction): DataState {
  switch (action.type) {
    case 'SET_DASHBOARD': return { ...state, dashboard: action.value }
    case 'SET_APPEALS': return { ...state, appeals: action.appeals, appealsTotal: action.total, appealsPages: action.pages }
    case 'UPDATE_APPEALS': return { ...state, appeals: action.updater(state.appeals) }
    case 'SET_SELECTED_APPEAL': return { ...state, selectedAppeal: action.value }
    case 'UPDATE_SELECTED_APPEAL': return { ...state, selectedAppeal: action.updater(state.selectedAppeal) }
    case 'SET_INSIGHTS': return { ...state, insights: action.value }
    case 'SET_DEADLINES': return { ...state, deadlines: action.deadlines, deadlineSummary: action.summary }
    case 'SET_CARRIER_CONTACT': return { ...state, carrierContact: action.value }
    case 'SET_TIMELINE_DATA': return { ...state, timelineData: action.value }
    case 'SET_NEXT_STEPS_DATA': return { ...state, nextStepsData: action.value }
    case 'SET_ADVANCE_RESULT': return { ...state, advanceResult: action.value }
    case 'SET_SELECTED_TIMELINE_EVENT': return { ...state, selectedTimelineEvent: action.value }
    case 'SET_P2P': return { ...state, p2pData: action.data, p2pText: action.text, p2pHtml: action.html }
    case 'RESET_P2P': return { ...state, p2pData: null, p2pText: '', p2pHtml: '' }
    case 'SET_ROI_DATA': return { ...state, roiData: action.value }
    case 'SET_TEAM_INFO': return { ...state, teamMembers: action.members, isTeamOwner: action.isOwner }
    case 'SET_STAFF_WORKLOAD': return { ...state, staffWorkload: action.value }
    case 'SET_MY_DEADLINES': return { ...state, myDeadlines: action.value }
    case 'SET_PRACTICE_REPORT': return { ...state, practiceReport: action.value }
    case 'SET_SELECTED_IDS': return { ...state, selectedIds: action.value }
    case 'TOGGLE_SELECT_APPEAL': {
      const next = new Set(state.selectedIds)
      if (next.has(action.id)) next.delete(action.id)
      else next.add(action.id)
      return { ...state, selectedIds: next }
    }
    case 'TOGGLE_SELECT_ALL': {
      if (state.selectedIds.size === state.appeals.length) {
        return { ...state, selectedIds: new Set() }
      }
      return { ...state, selectedIds: new Set(state.appeals.map(a => a.id)) }
    }
    case 'CLEAR_SELECTION': return { ...state, selectedIds: new Set() }
  }
}

// ── Filter State (search, filters, pagination, sort) ──────────────────────
interface FilterState {
  searchQuery: string
  outcomeFilter: string
  carrierFilter: string
  dateRange: string
  sortIndex: number
  appealsPage: number
  roiPeriod: string
  reportPeriod: string
}

type FilterAction =
  | { type: 'SET_SEARCH_QUERY'; value: string }
  | { type: 'SET_OUTCOME_FILTER'; value: string }
  | { type: 'SET_CARRIER_FILTER'; value: string }
  | { type: 'SET_DATE_RANGE'; value: string }
  | { type: 'SET_SORT_INDEX'; value: number }
  | { type: 'SET_APPEALS_PAGE'; value: number }
  | { type: 'SET_APPEALS_PAGE_FN'; updater: (prev: number) => number }
  | { type: 'SET_ROI_PERIOD'; value: string }
  | { type: 'SET_REPORT_PERIOD'; value: string }
  | { type: 'CLEAR_FILTERS' }

const filterInitialState: FilterState = {
  searchQuery: '',
  outcomeFilter: 'all',
  carrierFilter: '',
  dateRange: '',
  sortIndex: 0,
  appealsPage: 1,
  roiPeriod: 'month',
  reportPeriod: 'month',
}

function filterReducer(state: FilterState, action: FilterAction): FilterState {
  switch (action.type) {
    case 'SET_SEARCH_QUERY': return { ...state, searchQuery: action.value }
    case 'SET_OUTCOME_FILTER': return { ...state, outcomeFilter: action.value }
    case 'SET_CARRIER_FILTER': return { ...state, carrierFilter: action.value }
    case 'SET_DATE_RANGE': return { ...state, dateRange: action.value }
    case 'SET_SORT_INDEX': return { ...state, sortIndex: action.value }
    case 'SET_APPEALS_PAGE': return { ...state, appealsPage: action.value }
    case 'SET_APPEALS_PAGE_FN': return { ...state, appealsPage: action.updater(state.appealsPage) }
    case 'SET_ROI_PERIOD': return { ...state, roiPeriod: action.value }
    case 'SET_REPORT_PERIOD': return { ...state, reportPeriod: action.value }
    case 'CLEAR_FILTERS': return { ...filterInitialState, roiPeriod: state.roiPeriod, reportPeriod: state.reportPeriod }
  }
}

// ── Form State (inputs for various editing operations) ────────────────────
interface FormState {
  editingTagId: string | null
  editingTagValue: string
  feedbackAppealId: string | null
  feedbackPayerReason: string
  feedbackRequirements: string
  feedbackMissingDocs: string
  feedbackDenialCode: string
  feedbackResult: { lesson: string; improvements: string } | null
  editingDeadlineId: string | null
  deadlineDenialDate: string
  advanceNotes: string
  finClaimAmount: string
  finRecoveredAmount: string
  finAnnualValue: string
}

type FormAction =
  | { type: 'SET_EDITING_TAG'; id: string | null; value: string }
  | { type: 'SET_EDITING_TAG_VALUE'; value: string }
  | { type: 'OPEN_FEEDBACK_FORM'; appealId: string }
  | { type: 'SET_FEEDBACK_PAYER_REASON'; value: string }
  | { type: 'SET_FEEDBACK_REQUIREMENTS'; value: string }
  | { type: 'SET_FEEDBACK_MISSING_DOCS'; value: string }
  | { type: 'SET_FEEDBACK_DENIAL_CODE'; value: string }
  | { type: 'SET_FEEDBACK_RESULT'; value: { lesson: string; improvements: string } | null }
  | { type: 'SET_EDITING_DEADLINE'; id: string | null; denialDate: string }
  | { type: 'SET_DEADLINE_DENIAL_DATE'; value: string }
  | { type: 'SET_ADVANCE_NOTES'; value: string }
  | { type: 'SET_FIN_CLAIM_AMOUNT'; value: string }
  | { type: 'SET_FIN_RECOVERED_AMOUNT'; value: string }
  | { type: 'SET_FIN_ANNUAL_VALUE'; value: string }
  | { type: 'START_EDITING_FINANCIALS'; claimAmount: string; recoveredAmount: string; annualValue: string }

const formInitialState: FormState = {
  editingTagId: null,
  editingTagValue: '',
  feedbackAppealId: null,
  feedbackPayerReason: '',
  feedbackRequirements: '',
  feedbackMissingDocs: '',
  feedbackDenialCode: '',
  feedbackResult: null,
  editingDeadlineId: null,
  deadlineDenialDate: '',
  advanceNotes: '',
  finClaimAmount: '',
  finRecoveredAmount: '',
  finAnnualValue: '',
}

function formReducer(state: FormState, action: FormAction): FormState {
  switch (action.type) {
    case 'SET_EDITING_TAG': return { ...state, editingTagId: action.id, editingTagValue: action.value }
    case 'SET_EDITING_TAG_VALUE': return { ...state, editingTagValue: action.value }
    case 'OPEN_FEEDBACK_FORM': return { ...state, feedbackAppealId: action.appealId, feedbackPayerReason: '', feedbackRequirements: '', feedbackMissingDocs: '', feedbackDenialCode: '', feedbackResult: null }
    case 'SET_FEEDBACK_PAYER_REASON': return { ...state, feedbackPayerReason: action.value }
    case 'SET_FEEDBACK_REQUIREMENTS': return { ...state, feedbackRequirements: action.value }
    case 'SET_FEEDBACK_MISSING_DOCS': return { ...state, feedbackMissingDocs: action.value }
    case 'SET_FEEDBACK_DENIAL_CODE': return { ...state, feedbackDenialCode: action.value }
    case 'SET_FEEDBACK_RESULT': return { ...state, feedbackResult: action.value }
    case 'SET_EDITING_DEADLINE': return { ...state, editingDeadlineId: action.id, deadlineDenialDate: action.denialDate }
    case 'SET_DEADLINE_DENIAL_DATE': return { ...state, deadlineDenialDate: action.value }
    case 'SET_ADVANCE_NOTES': return { ...state, advanceNotes: action.value }
    case 'SET_FIN_CLAIM_AMOUNT': return { ...state, finClaimAmount: action.value }
    case 'SET_FIN_RECOVERED_AMOUNT': return { ...state, finRecoveredAmount: action.value }
    case 'SET_FIN_ANNUAL_VALUE': return { ...state, finAnnualValue: action.value }
    case 'START_EDITING_FINANCIALS': return { ...state, finClaimAmount: action.claimAmount, finRecoveredAmount: action.recoveredAmount, finAnnualValue: action.annualValue }
  }
}

export default function AppealsDashboard({ onClose, plan }: Props) {
  const [ui, dispatchUI] = useReducer(uiReducer, uiInitialState)
  const [data, dispatchData] = useReducer(dataReducer, dataInitialState)
  const [filters, dispatchFilters] = useReducer(filterReducer, filterInitialState)
  const [forms, dispatchForms] = useReducer(formReducer, formInitialState)
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Destructure for convenient access (preserves all variable names used in JSX)
  const { activeTab, loading, appealsLoading, detailLoading, insightsLoading, deadlinesLoading,
    timelineLoading, roiLoading, reportLoading, staffWorkloadLoading, p2pLoading,
    feedbackSubmitting, savingTag, savingFinancials, bulkUpdating, exporting, advancingAppeal,
    showFeedbackForm, showP2pModal, showAdvanceModal, editingFinancials,
    updatingOutcome, deadlineEscalating, deadlineSubmitting, assigningAppeal,
    p2pExpandedSections, carrierContactToast } = ui

  const { dashboard, appeals, appealsTotal, appealsPages, selectedAppeal, insights,
    deadlines, deadlineSummary, carrierContact, timelineData, nextStepsData,
    advanceResult, selectedTimelineEvent, p2pData, p2pText, p2pHtml,
    roiData, teamMembers, isTeamOwner, staffWorkload, practiceReport, selectedIds } = data

  const { searchQuery, outcomeFilter, carrierFilter, dateRange, sortIndex, appealsPage,
    roiPeriod, reportPeriod } = filters

  const { editingTagId, editingTagValue, feedbackAppealId, feedbackPayerReason,
    feedbackRequirements, feedbackMissingDocs, feedbackDenialCode, feedbackResult,
    editingDeadlineId, deadlineDenialDate, advanceNotes,
    finClaimAmount, finRecoveredAmount, finAnnualValue } = forms

  // Load dashboard data
  useEffect(() => {
    loadDashboard()
    loadDeadlines()
    loadTeamInfo()
  }, [])

  useEffect(() => {
    if (activeTab === 'deadlines') loadDeadlines()
    if (activeTab === 'appeals') loadAppeals()
    if (activeTab === 'insights') loadInsights()
    if (activeTab === 'roi') loadROIDashboard()
    if (activeTab === 'team') loadStaffWorkload()
    if (activeTab === 'report') loadPracticeReport()
  }, [activeTab, outcomeFilter, carrierFilter, dateRange, sortIndex, appealsPage])

  useEffect(() => {
    if (activeTab === 'roi') loadROIDashboard()
  }, [roiPeriod])

  useEffect(() => {
    if (activeTab === 'report') loadPracticeReport()
  }, [reportPeriod])

  // Debounced search
  const debouncedSearch = useCallback(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
    searchTimerRef.current = setTimeout(() => {
      dispatchFilters({ type: 'SET_APPEALS_PAGE', value: 1 })
      loadAppeals()
    }, 300)
  }, [outcomeFilter, carrierFilter, dateRange, sortIndex])

  useEffect(() => {
    if (activeTab === 'appeals') {
      debouncedSearch()
    }
    return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current) }
  }, [searchQuery])

  async function loadDashboard() {
    dispatchUI({ type: 'SET_LOADING', value: true })
    try {
      const resp = await apiAppealsDashboard()
      dispatchData({ type: 'SET_DASHBOARD', value: resp })
    } catch { }
    dispatchUI({ type: 'SET_LOADING', value: false })
  }

  async function loadDeadlines() {
    dispatchUI({ type: 'SET_DEADLINES_LOADING', value: true })
    try {
      const resp = await apiGetDeadlines()
      dispatchData({ type: 'SET_DEADLINES', deadlines: resp.deadlines, summary: resp.summary })
    } catch { }
    dispatchUI({ type: 'SET_DEADLINES_LOADING', value: false })
  }

  async function handleSetDenialDate(recordId: string, denialDate: string) {
    dispatchUI({ type: 'SET_DEADLINE_SUBMITTING', value: recordId })
    try {
      await apiUpdateDeadline(recordId, { denial_date: denialDate })
      await loadDeadlines()
      dispatchForms({ type: 'SET_EDITING_DEADLINE', id: null, denialDate: '' })
    } catch {
      alert('Failed to set deadline')
    }
    dispatchUI({ type: 'SET_DEADLINE_SUBMITTING', value: null })
  }

  async function handleMarkAppealSubmitted(recordId: string) {
    dispatchUI({ type: 'SET_DEADLINE_SUBMITTING', value: recordId })
    try {
      const now = new Date().toISOString()
      await apiUpdateDeadline(recordId, { appeal_submitted_date: now })
      await apiUpdateOutcome(recordId, { outcome: 'submitted' })
      await loadDeadlines()
      await loadDashboard()
    } catch {
      alert('Failed to mark as submitted')
    }
    dispatchUI({ type: 'SET_DEADLINE_SUBMITTING', value: null })
  }

  async function handleEscalateAppeal(recordId: string) {
    dispatchUI({ type: 'SET_DEADLINE_ESCALATING', value: recordId })
    try {
      await apiEscalateAppeal(recordId)
      await loadDeadlines()
      await loadDashboard()
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Failed to escalate appeal')
    }
    dispatchUI({ type: 'SET_DEADLINE_ESCALATING', value: null })
  }

  async function loadAppeals() {
    dispatchUI({ type: 'SET_APPEALS_LOADING', value: true })
    try {
      const sort = SORT_OPTIONS[sortIndex] || SORT_OPTIONS[0]
      const params: AppealsSearchParams = {
        page: appealsPage,
        per_page: 20,
        sort_by: sort.sort_by,
        sort_order: sort.sort_order,
      }
      if (searchQuery.trim()) params.q = searchQuery.trim()
      if (outcomeFilter !== 'all') params.outcome = outcomeFilter
      if (carrierFilter) params.carrier = carrierFilter
      if (dateRange) {
        const now = new Date()
        const from = new Date(now.getTime() - parseInt(dateRange) * 24 * 60 * 60 * 1000)
        params.from_date = from.toISOString().split('T')[0]
      }
      const resp = await apiAppealsSearch(params)
      dispatchData({ type: 'SET_APPEALS', appeals: resp.appeals, total: resp.total, pages: resp.pages })
    } catch {
      // Fallback to old history endpoint if search not available
      try {
        const resp = await apiAppealsHistory({
          outcome: outcomeFilter !== 'all' ? outcomeFilter : undefined,
          page: appealsPage,
          limit: 20,
        })
        dispatchData({ type: 'SET_APPEALS', appeals: resp.appeals, total: resp.total, pages: resp.pages })
      } catch { }
    }
    dispatchUI({ type: 'SET_APPEALS_LOADING', value: false })
  }

  async function loadInsights() {
    dispatchUI({ type: 'SET_INSIGHTS_LOADING', value: true })
    try {
      const resp = await apiAppealsInsights()
      dispatchData({ type: 'SET_INSIGHTS', value: resp.insights })
    } catch { }
    dispatchUI({ type: 'SET_INSIGHTS_LOADING', value: false })
  }

  async function loadROIDashboard() {
    dispatchUI({ type: 'SET_ROI_LOADING', value: true })
    try {
      const resp = await apiGetROIDashboard(roiPeriod)
      dispatchData({ type: 'SET_ROI_DATA', value: resp })
    } catch { }
    dispatchUI({ type: 'SET_ROI_LOADING', value: false })
  }

  async function loadPracticeReport() {
    dispatchUI({ type: 'SET_REPORT_LOADING', value: true })
    try {
      const resp = await apiGetPracticeReport(reportPeriod)
      dispatchData({ type: 'SET_PRACTICE_REPORT', value: resp })
    } catch {
      // silently ignore
    }
    dispatchUI({ type: 'SET_REPORT_LOADING', value: false })
  }

  function handleDownloadReport() {
    if (!practiceReport) return
    const r = practiceReport
    const s = r.executive_summary
    const lines: string[] = [
      r.report_title,
      'Generated: ' + new Date(r.generated_at).toLocaleDateString(),
      r.practice_name ? 'Practice: ' + r.practice_name : '',
      '',
      '=== EXECUTIVE SUMMARY ===',
      'Appeals Filed: ' + s.total_appeals_filed,
      'Appeals Won: ' + s.appeals_won,
      'Appeals Lost: ' + s.appeals_lost,
      'Appeals Pending: ' + s.appeals_pending,
      'Win Rate: ' + s.win_rate_pct + '%',
      'Total Claim Value: $' + s.total_claim_value.toLocaleString(),
      'Total Recovered: $' + s.total_recovered.toLocaleString(),
      'Recovery Rate: ' + s.recovery_rate_pct + '%',
      'Pending Pipeline: $' + s.pending_pipeline_value.toLocaleString(),
      'Subscription Cost: $' + s.subscription_cost.toLocaleString(),
      'ROI Multiple: ' + s.roi_multiple + 'x',
      'Time Saved: ' + s.time_saved_hours + ' hours',
      '',
      '=== CARRIER BREAKDOWN ===',
      ...r.carrier_breakdown.map(function(c) {
        return c.carrier + ': ' + c.appeals + ' appeals, ' + c.won + ' won (' + c.win_rate + '%), $' + c.recovered.toLocaleString() + ' recovered' + (c.avg_days_to_resolution ? ', avg ' + c.avg_days_to_resolution + ' days' : '')
      }),
      '',
      '=== DRUG BREAKDOWN ===',
      ...r.drug_breakdown.map(function(d) {
        return d.drug + ': ' + d.appeals + ' appeals, ' + d.won + ' won (' + d.win_rate + '%), $' + d.recovered.toLocaleString() + ' recovered'
      }),
    ]
    if (r.staff_performance.length > 0) {
      lines.push('', '=== STAFF PERFORMANCE ===')
      r.staff_performance.forEach(function(sp) {
        lines.push(sp.name + ': ' + sp.appeals_handled + ' appeals, ' + sp.won + ' won (' + sp.win_rate + '%), $' + sp.recovered.toLocaleString() + ' recovered')
      })
    }
    lines.push(
      '',
      '=== PERIOD OVER PERIOD CHANGES ===',
      'Appeals: ' + (r.month_over_month.appeals_change_pct >= 0 ? '+' : '') + r.month_over_month.appeals_change_pct + '%',
      'Win Rate: ' + (r.month_over_month.win_rate_change_pct >= 0 ? '+' : '') + r.month_over_month.win_rate_change_pct + '%',
      'Recovery: ' + (r.month_over_month.recovery_change_pct >= 0 ? '+' : '') + r.month_over_month.recovery_change_pct + '%',
      '',
      '=== KEY HIGHLIGHTS ===',
      ...r.highlights.map(function(h) { return '- ' + h }),
      '',
      'Report generated by RxCheckUp (rxcheckup.com)',
    )
    const text = lines.filter(function(l) { return l !== undefined }).join('\n')
    navigator.clipboard.writeText(text).then(function() {
      alert('Report copied to clipboard! You can paste it into an email or document.')
    }).catch(function() {
      const win = window.open('', '_blank')
      if (win) {
        win.document.write('<pre style="font-family: system-ui, sans-serif; font-size: 13px; line-height: 1.6; max-width: 800px; margin: 40px auto; white-space: pre-wrap;">' + text + '</pre>')
        win.document.close()
        win.print()
      }
    })
  }

  async function handleSaveFinancials() {
    if (!selectedAppeal) return
    dispatchUI({ type: 'SET_SAVING_FINANCIALS', value: true })
    try {
      const finData: { claim_amount?: number; recovered_amount?: number; estimated_annual_value?: number } = {}
      if (finClaimAmount !== '') finData.claim_amount = parseFloat(finClaimAmount)
      if (finRecoveredAmount !== '') finData.recovered_amount = parseFloat(finRecoveredAmount)
      if (finAnnualValue !== '') finData.estimated_annual_value = parseFloat(finAnnualValue)
      const result = await apiUpdateAppealFinancials(selectedAppeal.id, finData)
      dispatchData({ type: 'UPDATE_SELECTED_APPEAL', updater: prev => prev ? {
        ...prev,
        claim_amount: result.claim_amount,
        recovered_amount: result.recovered_amount,
        estimated_annual_value: result.estimated_annual_value,
      } : null })
      dispatchData({ type: 'UPDATE_APPEALS', updater: prev => prev.map(a => a.id === selectedAppeal.id ? {
        ...a,
        claim_amount: result.claim_amount,
        recovered_amount: result.recovered_amount,
        estimated_annual_value: result.estimated_annual_value,
      } : a) })
      dispatchUI({ type: 'SET_EDITING_FINANCIALS', value: false })
    } catch {
      alert('Failed to save financial data')
    }
    dispatchUI({ type: 'SET_SAVING_FINANCIALS', value: false })
  }

  function startEditingFinancials() {
    if (!selectedAppeal) return
    dispatchForms({ type: 'START_EDITING_FINANCIALS', claimAmount: selectedAppeal.claim_amount?.toString() || '', recoveredAmount: selectedAppeal.recovered_amount?.toString() || '', annualValue: selectedAppeal.estimated_annual_value?.toString() || '' })
    dispatchUI({ type: 'SET_EDITING_FINANCIALS', value: true })
  }

  async function handleViewDetail(id: string) {
    dispatchUI({ type: 'SET_DETAIL_LOADING', value: true })
    dispatchData({ type: 'SET_TIMELINE_DATA', value: null })
    dispatchData({ type: 'SET_NEXT_STEPS_DATA', value: null })
    dispatchData({ type: 'SET_ADVANCE_RESULT', value: null })
    dispatchData({ type: 'SET_SELECTED_TIMELINE_EVENT', value: null })
    try {
      const detail = await apiAppealDetail(id)
      dispatchData({ type: 'SET_SELECTED_APPEAL', value: detail })
      // Load timeline and next steps in parallel
      loadAppealWorkflow(id)
    } catch {
      alert('Failed to load appeal details')
    }
    dispatchUI({ type: 'SET_DETAIL_LOADING', value: false })
  }

  async function loadAppealWorkflow(id: string) {
    dispatchUI({ type: 'SET_TIMELINE_LOADING', value: true })
    try {
      const [timeline, nextSteps] = await Promise.all([
        apiAppealTimeline(id),
        apiAppealNextSteps(id),
      ])
      dispatchData({ type: 'SET_TIMELINE_DATA', value: timeline })
      dispatchData({ type: 'SET_NEXT_STEPS_DATA', value: nextSteps })
    } catch {
      // Timeline not available yet — that's OK for new appeals
    }
    dispatchUI({ type: 'SET_TIMELINE_LOADING', value: false })
  }

  async function handleAdvanceAppeal(action: 'denied' | 'approved') {
    if (!selectedAppeal) return
    dispatchUI({ type: 'SET_ADVANCING_APPEAL', value: true })
    try {
      const result = await apiAdvanceAppeal(selectedAppeal.id, {
        action,
        notes: advanceNotes.trim() || undefined,
      })
      dispatchData({ type: 'SET_ADVANCE_RESULT', value: result })
      dispatchUI({ type: 'SET_SHOW_ADVANCE_MODAL', value: false })
      dispatchForms({ type: 'SET_ADVANCE_NOTES', value: '' })
      // Reload everything
      await loadAppealWorkflow(selectedAppeal.id)
      const detail = await apiAppealDetail(selectedAppeal.id)
      dispatchData({ type: 'SET_SELECTED_APPEAL', value: detail })
      await loadDashboard()
      await loadAppeals()
    } catch {
      alert('Failed to advance appeal')
    }
    dispatchUI({ type: 'SET_ADVANCING_APPEAL', value: false })
  }

  // When an appeal detail loads, auto-lookup carrier contact info
  useEffect(() => {
    if (!selectedAppeal) { dispatchData({ type: 'SET_CARRIER_CONTACT', value: null }); return }
    let cancelled = false
    apiPayerDirectorySearch(selectedAppeal.carrier).then(result => {
      if (!cancelled && result.carriers.length > 0) dispatchData({ type: 'SET_CARRIER_CONTACT', value: result.carriers[0] })
    }).catch(() => {})
    return () => { cancelled = true }
  }, [selectedAppeal?.id])

  function copyCarrierContact(text: string, label: string) {
    navigator.clipboard.writeText(text)
    dispatchUI({ type: 'SET_CARRIER_CONTACT_TOAST', value: `${label} copied!` })
    setTimeout(() => dispatchUI({ type: 'SET_CARRIER_CONTACT_TOAST', value: null }), 2000)
  }

  async function handleUpdateOutcome(id: string, outcome: string) {
    dispatchUI({ type: 'SET_UPDATING_OUTCOME', value: id })
    try {
      await apiUpdateOutcome(id, { outcome })
      await loadDashboard()
      await loadAppeals()
      if (selectedAppeal?.id === id) {
        dispatchData({ type: 'UPDATE_SELECTED_APPEAL', updater: prev => prev ? { ...prev, outcome } : null })
      }
    } catch {
      alert('Failed to update outcome')
    }
    dispatchUI({ type: 'SET_UPDATING_OUTCOME', value: null })
  }

  async function handleSaveTag(id: string) {
    dispatchUI({ type: 'SET_SAVING_TAG', value: true })
    try {
      const result = await apiUpdateReferenceTag(id, editingTagValue.trim() || null)
      // Update in appeals list
      dispatchData({ type: 'UPDATE_APPEALS', updater: prev => prev.map(a => a.id === id ? { ...a, reference_tag: result.reference_tag } : a) })
      // Update in detail view if open
      if (selectedAppeal?.id === id) {
        dispatchData({ type: 'UPDATE_SELECTED_APPEAL', updater: prev => prev ? { ...prev, reference_tag: result.reference_tag } : null })
      }
      dispatchForms({ type: 'SET_EDITING_TAG', id: null, value: '' })
    } catch {
      alert('Failed to save reference tag')
    }
    dispatchUI({ type: 'SET_SAVING_TAG', value: false })
  }

  function startEditingTag(appeal: AppealItem) {
    dispatchForms({ type: 'SET_EDITING_TAG', id: appeal.id, value: appeal.reference_tag || '' })
  }

  function openFeedbackForm(appealId: string) {
    dispatchForms({ type: 'OPEN_FEEDBACK_FORM', appealId })
    dispatchUI({ type: 'SET_SHOW_FEEDBACK_FORM', value: true })
  }

  async function handleSubmitFeedback() {
    if (!feedbackAppealId || !feedbackPayerReason.trim()) return
    dispatchUI({ type: 'SET_FEEDBACK_SUBMITTING', value: true })
    try {
      const result = await apiSubmitDenialFeedback(feedbackAppealId, {
        payer_reason: feedbackPayerReason.trim(),
        payer_requirements: feedbackRequirements.trim() || undefined,
        missing_documentation: feedbackMissingDocs.trim() || undefined,
        payer_denial_code: feedbackDenialCode.trim() || undefined,
      })
      dispatchForms({ type: 'SET_FEEDBACK_RESULT', value: {
        lesson: result.lesson_learned,
        improvements: result.improvements,
      } })
      await loadDashboard()
      await loadAppeals()
    } catch {
      alert('Failed to submit feedback')
    }
    dispatchUI({ type: 'SET_FEEDBACK_SUBMITTING', value: false })
  }

  function clearFilters() {
    dispatchFilters({ type: 'CLEAR_FILTERS' })
  }

  async function handleGenerateP2P(recordId: string) {
    dispatchUI({ type: 'SET_P2P_LOADING', value: true })
    dispatchData({ type: 'RESET_P2P' })
    dispatchUI({ type: 'SET_SHOW_P2P_MODAL', value: true })
    dispatchUI({ type: 'SET_P2P_EXPANDED_SECTIONS', value: { ...DEFAULT_P2P_SECTIONS } })
    try {
      const result = await apiGenerateP2PFromRecord(recordId)
      dispatchData({ type: 'SET_P2P', data: result.p2p_data, text: result.text, html: result.html })
    } catch {
      alert('Failed to generate P2P prep sheet')
      dispatchUI({ type: 'SET_SHOW_P2P_MODAL', value: false })
    }
    dispatchUI({ type: 'SET_P2P_LOADING', value: false })
  }

  function toggleP2pSection(section: string) {
    dispatchUI({ type: 'TOGGLE_P2P_SECTION', section })
  }

  function handlePrintP2P() {
    const printWindow = window.open('', '_blank')
    if (printWindow) {
      printWindow.document.write(`<!DOCTYPE html><html><head><title>P2P Prep Sheet</title><style>
        @media print { body { margin: 0; } @page { margin: 1cm; } }
      </style></head><body>${p2pHtml}</body></html>`)
      printWindow.document.close()
      printWindow.focus()
      setTimeout(() => printWindow.print(), 300)
    }
  }

  async function handleDownloadP2PPDF() {
    if (!p2pText) return
    const blob = new Blob([p2pText], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    const drugName = p2pData?.metadata?.drug?.replace(/\s+/g, '_') || 'drug'
    const carrierName = p2pData?.metadata?.carrier?.replace(/\s+/g, '_') || 'carrier'
    a.download = `P2P_Prep_${drugName}_${carrierName}.txt`
    a.click()
    URL.revokeObjectURL(url)
  }

  // ── Team info loader ──
  async function loadTeamInfo() {
    try {
      const resp = await apiGetTeam()
      dispatchData({ type: 'SET_TEAM_INFO', members: resp.members, isOwner: resp.members.length > 0 })
    } catch {
      dispatchData({ type: 'SET_TEAM_INFO', members: [], isOwner: false })
    }
  }

  // ── Staff workload loader ──
  async function loadStaffWorkload() {
    if (!isTeamOwner) return
    dispatchUI({ type: 'SET_STAFF_WORKLOAD_LOADING', value: true })
    try {
      const resp = await apiGetStaffWorkload()
      dispatchData({ type: 'SET_STAFF_WORKLOAD', value: resp.staff })
    } catch { }
    dispatchUI({ type: 'SET_STAFF_WORKLOAD_LOADING', value: false })
  }

  // ── Bulk operations ──
  function toggleSelectAppeal(id: string) {
    dispatchData({ type: 'TOGGLE_SELECT_APPEAL', id })
  }

  function toggleSelectAll() {
    dispatchData({ type: 'TOGGLE_SELECT_ALL' })
  }

  async function handleBulkUpdate(outcome: string) {
    if (selectedIds.size === 0) return
    dispatchUI({ type: 'SET_BULK_UPDATING', value: true })
    try {
      await apiBulkUpdateAppeals(Array.from(selectedIds), { outcome })
      dispatchData({ type: 'CLEAR_SELECTION' })
      await loadAppeals()
      await loadDashboard()
    } catch {
      alert('Failed to update selected appeals')
    }
    dispatchUI({ type: 'SET_BULK_UPDATING', value: false })
  }

  async function handleExportCSV() {
    dispatchUI({ type: 'SET_EXPORTING', value: true })
    try {
      const blob = await apiExportAppeals({
        carrier: carrierFilter || undefined,
        outcome: outcomeFilter !== 'all' ? outcomeFilter : undefined,
      })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'appeals_export.csv'
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      alert('Failed to export appeals')
    }
    dispatchUI({ type: 'SET_EXPORTING', value: false })
  }

  async function handleAssignAppeal(recordId: string, assignedToId: string | null) {
    dispatchUI({ type: 'SET_ASSIGNING_APPEAL', value: recordId })
    try {
      const result = await apiAssignAppeal(recordId, assignedToId)
      if (selectedAppeal?.id === recordId) {
        dispatchData({ type: 'UPDATE_SELECTED_APPEAL', updater: prev => prev ? { ...prev, assigned_to_id: result.assigned_to_id, assigned_to_name: result.assigned_to_name } as any : null })
      }
      await loadAppeals()
    } catch {
      alert('Failed to assign appeal')
    }
    dispatchUI({ type: 'SET_ASSIGNING_APPEAL', value: null })
  }

  const hasActiveFilters = searchQuery || outcomeFilter !== 'all' || carrierFilter || dateRange || sortIndex !== 0

  // Pagination helpers
  const startItem = (appealsPage - 1) * 20 + 1
  const endItem = Math.min(appealsPage * 20, appealsTotal)

  if (loading && !dashboard) {
    return (
      <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center">
        <div className="bg-white rounded-2xl p-12 text-center">
          <div className="w-8 h-8 border-3 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-500">Loading Appeals Dashboard...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 bg-slate-50 overflow-auto">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center text-white">
              <svg className="w-4.5 h-4.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25z" />
              </svg>
            </div>
            <div>
              <h1 className="text-lg font-bold text-slate-800">Appeals Tracker</h1>
              <p className="text-xs text-slate-400">Track outcomes, learn from denials, improve win rates · <span className="text-amber-500 font-semibold">Records auto-delete after 6 weeks</span></p>
            </div>
          </div>
          <button onClick={onClose}
            className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 font-medium">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
            </svg>
            Back to App
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-6">
        {/* Tabs */}
        <div className="flex gap-1 bg-white rounded-xl border border-slate-200 p-1 mb-6 w-fit">
          {(['deadlines', 'overview', 'appeals', 'roi', 'report', 'insights', ...(isTeamOwner ? ['team' as const] : [])] as const).map(tab => (
            <button key={tab} onClick={() => dispatchUI({ type: 'SET_ACTIVE_TAB', tab: tab as TabId })}
              className={`px-5 py-2 rounded-lg text-sm font-semibold transition-colors relative ${
                activeTab === tab
                  ? tab === 'roi' ? 'bg-emerald-600 text-white shadow-sm'
                  : tab === 'report' ? 'bg-emerald-700 text-white shadow-sm'
                  : tab === 'team' ? 'bg-indigo-600 text-white shadow-sm'
                  : 'bg-teal-500 text-white shadow-sm'
                  : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
              }`}>
              {tab === 'deadlines' ? 'Deadlines' : tab === 'overview' ? 'Overview' : tab === 'appeals' ? 'My Appeals' : tab === 'roi' ? 'ROI' : tab === 'report' ? 'Report' : tab === 'insights' ? 'Insights' : 'Team'}
              {tab === 'deadlines' && deadlineSummary && (deadlineSummary.overdue > 0 || deadlineSummary.due_this_week > 0) && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-[10px] text-white flex items-center justify-center font-bold">
                  {deadlineSummary.overdue + deadlineSummary.due_this_week}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* ── DEADLINES TAB ── */}
        {activeTab === 'deadlines' && (
          <div className="space-y-6">
            {/* Summary bar */}
            {deadlineSummary && (
              <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-wrap items-center gap-4 text-sm">
                {deadlineSummary.overdue > 0 && (
                  <span className="flex items-center gap-1.5 text-red-600 font-bold">
                    <span className="w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse" />
                    {deadlineSummary.overdue} overdue
                  </span>
                )}
                {deadlineSummary.due_this_week > 0 && (
                  <span className="flex items-center gap-1.5 text-red-600 font-semibold">
                    <span className="w-2 h-2 bg-red-400 rounded-full" />
                    {deadlineSummary.due_this_week} due this week
                  </span>
                )}
                {deadlineSummary.due_two_weeks > 0 && (
                  <span className="flex items-center gap-1.5 text-amber-600 font-semibold">
                    <span className="w-2 h-2 bg-amber-400 rounded-full" />
                    {deadlineSummary.due_two_weeks} due in 2 weeks
                  </span>
                )}
                <span className="text-slate-500">
                  {deadlineSummary.total_active} active appeal{deadlineSummary.total_active !== 1 ? 's' : ''} with deadlines
                </span>
              </div>
            )}

            {deadlinesLoading ? (
              <div className="text-center py-12">
                <div className="w-8 h-8 border-3 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                <p className="text-sm text-slate-400">Loading deadlines...</p>
              </div>
            ) : deadlines.length === 0 ? (
              <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
                  <svg className="w-7 h-7 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-bold text-slate-700 mb-1">No Active Deadlines</h3>
                <p className="text-sm text-slate-400 max-w-md mx-auto">
                  Set a denial date on any appeal to start tracking its deadline. Go to the{' '}
                  <button onClick={() => dispatchUI({ type: 'SET_ACTIVE_TAB', tab: 'appeals' })} className="text-teal-600 underline font-medium">My Appeals</button>{' '}
                  tab and update an appeal to get started.
                </p>
              </div>
            ) : (
              <div className="grid gap-4">
                {deadlines.map(dl => (
                  <div key={dl.id} className={`bg-white rounded-xl border ${
                    dl.status === 'overdue' ? 'border-red-300 bg-red-50/30' :
                    dl.status === 'critical' ? 'border-red-200' :
                    dl.status === 'warning' ? 'border-amber-200' :
                    'border-slate-200'
                  } p-5`}>
                    <div className="flex items-start justify-between gap-4">
                      {/* Left: info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-bold text-slate-800 truncate">{dl.denied_product}</h3>
                          {dl.status === 'overdue' || dl.status === 'critical' ? (
                            <span className="text-xs px-1.5 py-0.5 rounded font-bold bg-red-100 text-red-700" title="Critical deadline">&#x1F534;</span>
                          ) : dl.status === 'warning' ? (
                            <span className="text-xs px-1.5 py-0.5 rounded font-bold bg-amber-100 text-amber-700" title="Urgent deadline">&#x1F7E1;</span>
                          ) : (
                            <span className="text-xs px-1.5 py-0.5 rounded font-bold bg-emerald-100 text-emerald-700" title="Upcoming deadline">&#x1F7E2;</span>
                          )}
                          <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                            dl.outcome === 'submitted' ? 'bg-blue-100 text-blue-700' :
                            dl.outcome === 'denied' ? 'bg-red-100 text-red-700' :
                            'bg-slate-100 text-slate-600'
                          }`}>{dl.outcome === 'pending' ? 'Not Submitted' : dl.outcome}</span>
                        </div>
                        <div className="flex items-center gap-3 text-sm text-slate-500 mb-2">
                          <span>{dl.carrier}</span>
                          <span className="text-slate-300">|</span>
                          <span className="font-medium text-slate-600">{dl.appeal_level_label}</span>
                          {dl.reference_tag && (
                            <>
                              <span className="text-slate-300">|</span>
                              <span className="text-teal-600 font-medium">{dl.reference_tag}</span>
                            </>
                          )}
                        </div>
                        {dl.appeal_deadline && (
                          <p className="text-xs text-slate-400">
                            Deadline: {new Date(dl.appeal_deadline).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                            {dl.denial_date && (
                              <span className="ml-3">Denial received: {new Date(dl.denial_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                            )}
                          </p>
                        )}
                      </div>

                      {/* Right: days remaining badge */}
                      <div className="flex-shrink-0 text-center">
                        <div className={`w-16 h-16 rounded-xl flex flex-col items-center justify-center ${
                          dl.status === 'overdue' ? 'bg-red-100' :
                          dl.status === 'critical' ? 'bg-red-50' :
                          dl.status === 'warning' ? 'bg-amber-50' :
                          'bg-emerald-50'
                        }`}>
                          <span className={`text-2xl font-extrabold ${
                            dl.status === 'overdue' ? 'text-red-600' :
                            dl.status === 'critical' ? 'text-red-600' :
                            dl.status === 'warning' ? 'text-amber-600' :
                            'text-emerald-600'
                          }`}>
                            {dl.days_remaining !== null ? (dl.days_remaining < 0 ? Math.abs(dl.days_remaining) : dl.days_remaining) : '--'}
                          </span>
                          <span className={`text-[10px] font-semibold uppercase tracking-wider ${
                            dl.status === 'overdue' ? 'text-red-500' :
                            dl.status === 'critical' ? 'text-red-500' :
                            dl.status === 'warning' ? 'text-amber-500' :
                            'text-emerald-500'
                          }`}>
                            {dl.status === 'overdue' ? 'overdue' : 'days'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Action buttons */}
                    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100">
                      {dl.outcome !== 'submitted' && (
                        <button
                          onClick={() => handleMarkAppealSubmitted(dl.id)}
                          disabled={deadlineSubmitting === dl.id}
                          className="text-xs px-3 py-1.5 rounded-lg bg-blue-50 text-blue-700 font-semibold hover:bg-blue-100 transition-colors disabled:opacity-50"
                        >
                          {deadlineSubmitting === dl.id ? 'Saving...' : 'Mark as Submitted'}
                        </button>
                      )}
                      <button
                        onClick={() => handleEscalateAppeal(dl.id)}
                        disabled={deadlineEscalating === dl.id}
                        className="text-xs px-3 py-1.5 rounded-lg bg-amber-50 text-amber-700 font-semibold hover:bg-amber-100 transition-colors disabled:opacity-50"
                      >
                        {deadlineEscalating === dl.id ? 'Escalating...' : 'Escalate to Next Level'}
                      </button>
                      <button
                        onClick={() => handleViewDetail(dl.id)}
                        className="text-xs px-3 py-1.5 rounded-lg bg-slate-50 text-slate-600 font-semibold hover:bg-slate-100 transition-colors"
                      >
                        View Detail
                      </button>
                      {!dl.denial_date && (
                        editingDeadlineId === dl.id ? (
                          <div className="flex items-center gap-1.5 ml-auto">
                            <input
                              type="date"
                              value={deadlineDenialDate}
                              onChange={e => dispatchForms({ type: 'SET_DEADLINE_DENIAL_DATE', value: e.target.value })}
                              className="text-xs px-2 py-1 border border-slate-300 rounded-lg"
                            />
                            <button
                              onClick={() => deadlineDenialDate && handleSetDenialDate(dl.id, deadlineDenialDate)}
                              disabled={!deadlineDenialDate || deadlineSubmitting === dl.id}
                              className="text-xs px-2 py-1 rounded-lg bg-teal-500 text-white font-semibold disabled:opacity-50"
                            >
                              Set
                            </button>
                            <button
                              onClick={() => dispatchForms({ type: 'SET_EDITING_DEADLINE', id: null, denialDate: '' })}
                              className="text-xs px-2 py-1 text-slate-400 hover:text-slate-600"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => dispatchForms({ type: 'SET_EDITING_DEADLINE', id: dl.id, denialDate: '' })}
                            className="text-xs px-3 py-1.5 rounded-lg bg-teal-50 text-teal-700 font-semibold hover:bg-teal-100 transition-colors ml-auto"
                          >
                            Set Denial Date
                          </button>
                        )
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── OVERVIEW TAB ── */}
        {activeTab === 'overview' && dashboard && (
          <div className="space-y-6">
            {/* Stats cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard label="Total LMNs" value={dashboard.total_lmns} color="slate" />
              <StatCard label="Submitted" value={dashboard.submitted} color="blue" />
              <StatCard label="Approved" value={dashboard.approved} color="emerald" />
              <StatCard label="Denied" value={dashboard.denied} color="red" />
            </div>

            {/* Win rate + pending */}
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Appeal Win Rate</p>
                <p className={`text-3xl font-extrabold ${
                  dashboard.win_rate === null ? 'text-slate-300'
                  : dashboard.win_rate >= 70 ? 'text-emerald-600'
                  : dashboard.win_rate >= 40 ? 'text-amber-600'
                  : 'text-red-600'
                }`}>
                  {dashboard.win_rate !== null ? `${dashboard.win_rate}%` : '\u2014'}
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  {dashboard.win_rate !== null
                    ? `${dashboard.approved} approved of ${dashboard.approved + dashboard.denied} decided`
                    : 'No outcomes recorded yet'}
                </p>
              </div>

              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Awaiting Outcome</p>
                <p className="text-3xl font-extrabold text-blue-600">{dashboard.pending + dashboard.submitted}</p>
                <p className="text-xs text-slate-400 mt-1">
                  {dashboard.pending} not yet submitted &bull; {dashboard.submitted} with payer
                </p>
              </div>

              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Learning Insights</p>
                <p className="text-3xl font-extrabold text-violet-600">{dashboard.feedback_count}</p>
                <p className="text-xs text-slate-400 mt-1">
                  Denial feedback entries improving future LMNs
                </p>
              </div>
            </div>

            {/* Top denied */}
            {(dashboard.top_denied_carriers.length > 0 || dashboard.top_denied_drugs.length > 0) && (
              <div className="grid md:grid-cols-2 gap-4">
                {dashboard.top_denied_carriers.length > 0 && (
                  <div className="bg-white rounded-xl border border-slate-200 p-5">
                    <p className="text-xs font-bold text-red-400 uppercase tracking-wider mb-3">Top Denying Carriers</p>
                    {dashboard.top_denied_carriers.map((c, i) => (
                      <div key={i} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                        <span className="text-sm text-slate-700">{c.carrier}</span>
                        <span className="text-sm font-bold text-red-600">{c.count} denied</span>
                      </div>
                    ))}
                  </div>
                )}
                {dashboard.top_denied_drugs.length > 0 && (
                  <div className="bg-white rounded-xl border border-slate-200 p-5">
                    <p className="text-xs font-bold text-red-400 uppercase tracking-wider mb-3">Most Denied Medications</p>
                    {dashboard.top_denied_drugs.map((d, i) => (
                      <div key={i} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                        <span className="text-sm text-slate-700">{d.drug}</span>
                        <span className="text-sm font-bold text-red-600">{d.count} denied</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Recent outcomes */}
            {dashboard.recent_outcomes.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Recent Outcomes</p>
                <div className="space-y-2">
                  {dashboard.recent_outcomes.map(r => (
                    <div key={r.id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                      <div className="flex items-center gap-3">
                        <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${OUTCOME_STYLES[r.outcome]}`}>
                          {OUTCOME_LABELS[r.outcome]}
                        </span>
                        <div>
                          <p className="text-sm font-semibold text-slate-700">{r.denied_product}</p>
                          <p className="text-xs text-slate-400">{r.carrier}</p>
                        </div>
                      </div>
                      <span className="text-xs text-slate-400">
                        {r.outcome_updated_at ? new Date(r.outcome_updated_at).toLocaleDateString() : ''}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Empty state */}
            {dashboard.total_lmns === 0 && (
              <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                <div className="w-16 h-16 rounded-full bg-teal-50 flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-teal-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                  </svg>
                </div>
                <h3 className="text-lg font-bold text-slate-800 mb-2">No Appeals Yet</h3>
                <p className="text-sm text-slate-500 max-w-md mx-auto">
                  Generate your first LMN, then track its outcome here. When appeals are denied,
                  you can provide feedback that helps create stronger letters next time.
                </p>
              </div>
            )}
          </div>
        )}

        {/* ── APPEALS TAB ── */}
        {activeTab === 'appeals' && (
          <div className="space-y-4">
            {/* Search bar */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                </svg>
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={e => dispatchFilters({ type: 'SET_SEARCH_QUERY', value: e.target.value })}
                placeholder="Search by drug, carrier, tag, or ICD code..."
                className="w-full pl-11 pr-4 py-3 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 transition-all placeholder:text-slate-400"
              />
              {searchQuery && (
                <button onClick={() => dispatchFilters({ type: 'SET_SEARCH_QUERY', value: '' })}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-slate-600">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>

            {/* Filter row */}
            <div className="flex gap-3 flex-wrap items-center">
              {/* Outcome pills */}
              <div className="flex gap-1.5">
                {['all', 'pending', 'submitted', 'approved', 'denied'].map(status => (
                  <button key={status} onClick={() => { dispatchFilters({ type: 'SET_OUTCOME_FILTER', value: status }); dispatchFilters({ type: 'SET_APPEALS_PAGE', value: 1 }) }}
                    className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                      outcomeFilter === status
                        ? status === 'all' ? 'bg-teal-500 text-white'
                          : status === 'approved' ? 'bg-emerald-500 text-white'
                          : status === 'denied' ? 'bg-red-500 text-white'
                          : status === 'submitted' ? 'bg-blue-500 text-white'
                          : 'bg-slate-600 text-white'
                        : 'bg-white border border-slate-200 text-slate-500 hover:bg-slate-50'
                    }`}>
                    {status === 'all' ? 'All' : OUTCOME_LABELS[status]}
                  </button>
                ))}
              </div>

              {/* Carrier dropdown */}
              <select
                value={carrierFilter}
                onChange={e => { dispatchFilters({ type: 'SET_CARRIER_FILTER', value: e.target.value }); dispatchFilters({ type: 'SET_APPEALS_PAGE', value: 1 }) }}
                className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-semibold text-slate-600 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400">
                <option value="">All Carriers</option>
                {CARRIERS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>

              {/* Date range */}
              <select
                value={dateRange}
                onChange={e => { dispatchFilters({ type: 'SET_DATE_RANGE', value: e.target.value }); dispatchFilters({ type: 'SET_APPEALS_PAGE', value: 1 }) }}
                className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-semibold text-slate-600 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400">
                {DATE_RANGES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
              </select>

              {/* Sort */}
              <select
                value={sortIndex}
                onChange={e => { dispatchFilters({ type: 'SET_SORT_INDEX', value: parseInt(e.target.value) }); dispatchFilters({ type: 'SET_APPEALS_PAGE', value: 1 }) }}
                className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-semibold text-slate-600 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400">
                {SORT_OPTIONS.map((s, i) => <option key={i} value={i}>{s.label}</option>)}
              </select>

              {/* Clear filters */}
              {hasActiveFilters && (
                <button onClick={clearFilters}
                  className="px-3 py-1.5 text-xs font-semibold text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors">
                  Clear Filters
                </button>
              )}
            </div>

            {/* Results count + Export button */}
            <div className="flex items-center justify-between">
              <p className="text-xs text-slate-400">
                {appealsTotal > 0
                  ? `Showing ${startItem}\u2013${endItem} of ${appealsTotal} results`
                  : 'No results'}
              </p>
              <div className="flex items-center gap-2">
                {appealsLoading && (
                  <div className="flex items-center gap-1.5 text-xs text-teal-500">
                    <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Searching...
                  </div>
                )}
                <button
                  onClick={handleExportCSV}
                  disabled={exporting || appealsTotal === 0}
                  className="px-3 py-1.5 text-xs font-semibold text-teal-700 bg-teal-50 border border-teal-200 rounded-lg hover:bg-teal-100 disabled:opacity-50 flex items-center gap-1.5"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                  </svg>
                  {exporting ? 'Exporting...' : 'Export All (CSV)'}
                </button>
              </div>
            </div>

            {/* Bulk operations toolbar */}
            {selectedIds.size > 0 && (
              <div className="bg-teal-50 border border-teal-200 rounded-xl px-4 py-3 flex items-center justify-between">
                <p className="text-sm font-semibold text-teal-700">{selectedIds.size} appeal{selectedIds.size !== 1 ? 's' : ''} selected</p>
                <div className="flex items-center gap-2">
                  <button onClick={() => handleBulkUpdate('submitted')} disabled={bulkUpdating}
                    className="px-3 py-1.5 text-xs font-semibold bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 disabled:opacity-50">
                    Mark Submitted
                  </button>
                  <button onClick={() => handleBulkUpdate('approved')} disabled={bulkUpdating}
                    className="px-3 py-1.5 text-xs font-semibold bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200 disabled:opacity-50">
                    Mark Approved
                  </button>
                  <button onClick={() => handleBulkUpdate('denied')} disabled={bulkUpdating}
                    className="px-3 py-1.5 text-xs font-semibold bg-red-100 text-red-700 rounded-lg hover:bg-red-200 disabled:opacity-50">
                    Mark Denied
                  </button>
                  <button onClick={() => dispatchData({ type: 'CLEAR_SELECTION' })}
                    className="px-3 py-1.5 text-xs font-semibold text-slate-500 hover:text-slate-700">
                    Clear Selection
                  </button>
                </div>
              </div>
            )}

            {/* Appeals list */}
            <div className={`bg-white rounded-xl border border-slate-200 overflow-hidden transition-opacity ${appealsLoading ? 'opacity-60' : ''}`}>
              {appeals.length === 0 ? (
                <div className="p-8 text-center text-sm text-slate-400">
                  {hasActiveFilters ? 'No appeals match your search or filters.' : 'No appeals yet.'}
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-100 text-xs text-slate-400 uppercase tracking-wider">
                      <th className="p-4 w-8">
                        <input
                          type="checkbox"
                          checked={selectedIds.size === appeals.length && appeals.length > 0}
                          onChange={toggleSelectAll}
                          className="rounded border-slate-300 text-teal-500 focus:ring-teal-500/30"
                        />
                      </th>
                      <th className="text-left p-4">Drug / Carrier</th>
                      <th className="text-left p-4">Tag</th>
                      <th className="text-left p-4">Denial Reason</th>
                      <th className="text-center p-4">Status</th>
                      <th className="text-right p-4">$</th>
                      <th className="text-left p-4">Date</th>
                      <th className="text-right p-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {appeals.map(a => (
                      <tr key={a.id} className={`border-b border-slate-50 hover:bg-slate-50/50 ${selectedIds.has(a.id) ? 'bg-teal-50/50' : ''}`}>
                        <td className="p-4 w-8">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(a.id)}
                            onChange={() => toggleSelectAppeal(a.id)}
                            className="rounded border-slate-300 text-teal-500 focus:ring-teal-500/30"
                          />
                        </td>
                        <td className="p-4">
                          <p className="font-semibold text-slate-800">{a.denied_product}</p>
                          <p className="text-xs text-slate-400">{a.carrier}</p>
                        </td>
                        <td className="p-4">
                          {editingTagId === a.id ? (
                            <div>
                              <div className="flex items-center gap-1">
                              <input
                                type="text"
                                value={editingTagValue}
                                onChange={e => dispatchForms({ type: 'SET_EDITING_TAG_VALUE', value: e.target.value })}
                                onKeyDown={e => { if (e.key === 'Enter') handleSaveTag(a.id); if (e.key === 'Escape') dispatchForms({ type: 'SET_EDITING_TAG', id: null, value: '' }) }}
                                maxLength={100}
                                placeholder="e.g., Case-Humira-April"
                                className="w-36 px-2 py-1 border border-teal-300 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-teal-500"
                                autoFocus
                              />
                              <button onClick={() => handleSaveTag(a.id)} disabled={savingTag}
                                className="text-teal-600 hover:text-teal-800 disabled:opacity-50" title="Save">
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                              </button>
                              <button onClick={() => dispatchForms({ type: 'SET_EDITING_TAG', id: null, value: '' })}
                                className="text-slate-400 hover:text-slate-600" title="Cancel">
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                              </button>
                              </div>
                              <p className="text-[11px] text-amber-600 mt-1">&#9888; Do not include patient names or initials. Use non-identifying labels only (e.g., 'Case-Humira-April').</p>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1.5 group">
                              {a.reference_tag ? (
                                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-teal-50 text-teal-700 border border-teal-200">
                                  {a.reference_tag}
                                </span>
                              ) : (
                                <span className="text-xs text-slate-300 italic">No tag</span>
                              )}
                              <button onClick={() => startEditingTag(a)}
                                className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-teal-600 transition-opacity" title="Edit tag">
                                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
                                </svg>
                              </button>
                            </div>
                          )}
                        </td>
                        <td className="p-4 text-slate-600 text-xs">{a.denial_reason.replace(/_/g, ' ')}</td>
                        <td className="p-4 text-center">
                          <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${OUTCOME_STYLES[a.outcome]}`}>
                            {OUTCOME_LABELS[a.outcome]}
                          </span>
                          {a.has_feedback && (
                            <span className="ml-1 text-xs text-violet-500" title="Has learning feedback">*</span>
                          )}
                        </td>
                        <td className="p-4 text-right">
                          {a.recovered_amount ? (
                            <span className="text-xs font-bold text-emerald-600">${a.recovered_amount.toLocaleString()}</span>
                          ) : a.claim_amount ? (
                            <span className="text-xs text-slate-500">${a.claim_amount.toLocaleString()}</span>
                          ) : (
                            <span className="text-xs text-slate-300">&mdash;</span>
                          )}
                        </td>
                        <td className="p-4 text-xs text-slate-400">
                          {new Date(a.created_at).toLocaleDateString()}
                        </td>
                        <td className="p-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            {a.outcome === 'pending' && (
                              <button onClick={() => handleUpdateOutcome(a.id, 'submitted')}
                                disabled={updatingOutcome === a.id}
                                className="text-xs font-semibold text-blue-600 hover:text-blue-700 disabled:opacity-50">
                                Mark Submitted
                              </button>
                            )}
                            {a.outcome === 'submitted' && (
                              <>
                                <button onClick={() => handleUpdateOutcome(a.id, 'approved')}
                                  disabled={updatingOutcome === a.id}
                                  className="text-xs font-semibold text-emerald-600 hover:text-emerald-700 disabled:opacity-50">
                                  Approved
                                </button>
                                <button onClick={() => handleUpdateOutcome(a.id, 'denied')}
                                  disabled={updatingOutcome === a.id}
                                  className="text-xs font-semibold text-red-600 hover:text-red-700 disabled:opacity-50">
                                  Denied
                                </button>
                              </>
                            )}
                            {a.outcome === 'denied' && !a.has_feedback && (
                              <button onClick={() => openFeedbackForm(a.id)}
                                className="text-xs font-semibold text-violet-600 hover:text-violet-700">
                                Add Feedback
                              </button>
                            )}
                            <button onClick={() => handleViewDetail(a.id)}
                              className="text-xs font-semibold text-slate-500 hover:text-slate-700">
                              View
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination */}
            {appealsPages > 1 && (
              <div className="flex items-center justify-center gap-2">
                <button onClick={() => dispatchFilters({ type: 'SET_APPEALS_PAGE_FN', updater: p => Math.max(1, p - 1) })} disabled={appealsPage === 1}
                  className="px-3 py-1.5 text-xs font-semibold text-slate-500 bg-white border border-slate-200 rounded-lg disabled:opacity-50 hover:bg-slate-50">
                  Previous
                </button>
                {Array.from({ length: Math.min(appealsPages, 7) }, (_, i) => {
                  // Show first page, last page, and pages around current
                  let pageNum: number
                  if (appealsPages <= 7) {
                    pageNum = i + 1
                  } else if (appealsPage <= 4) {
                    pageNum = i + 1
                  } else if (appealsPage >= appealsPages - 3) {
                    pageNum = appealsPages - 6 + i
                  } else {
                    pageNum = appealsPage - 3 + i
                  }
                  return (
                    <button key={pageNum} onClick={() => dispatchFilters({ type: 'SET_APPEALS_PAGE', value: pageNum })}
                      className={`w-8 h-8 rounded-lg text-xs font-semibold ${
                        appealsPage === pageNum
                          ? 'bg-teal-500 text-white'
                          : 'text-slate-500 bg-white border border-slate-200 hover:bg-slate-50'
                      }`}>
                      {pageNum}
                    </button>
                  )
                })}
                <button onClick={() => dispatchFilters({ type: 'SET_APPEALS_PAGE_FN', updater: p => Math.min(appealsPages, p + 1) })} disabled={appealsPage === appealsPages}
                  className="px-3 py-1.5 text-xs font-semibold text-slate-500 bg-white border border-slate-200 rounded-lg disabled:opacity-50 hover:bg-slate-50">
                  Next
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── ROI TAB ── */}
        {activeTab === 'roi' && (
          <div className="space-y-6">
            {/* Period selector */}
            <div className="flex items-center gap-2">
              {[
                { label: 'Month', value: 'month' },
                { label: 'Quarter', value: 'quarter' },
                { label: 'Year', value: 'year' },
                { label: 'All Time', value: 'all' },
              ].map(p => (
                <button key={p.value} onClick={() => dispatchFilters({ type: 'SET_ROI_PERIOD', value: p.value })}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                    roiPeriod === p.value
                      ? 'bg-emerald-600 text-white shadow-sm'
                      : 'bg-white border border-slate-200 text-slate-500 hover:bg-slate-50'
                  }`}>
                  {p.label}
                </button>
              ))}
            </div>

            {roiLoading ? (
              <div className="text-center py-12">
                <div className="w-8 h-8 border-3 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                <p className="text-sm text-slate-400">Loading ROI data...</p>
              </div>
            ) : roiData ? (
              <>
                {/* Hero metrics */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white rounded-xl border border-emerald-200 p-5">
                    <p className="text-xs font-bold text-emerald-500 uppercase tracking-wider mb-1">Total Recovered</p>
                    <p className="text-3xl font-extrabold text-emerald-600">${roiData.total_recovered.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</p>
                    <p className="text-xs text-slate-400 mt-1">{roiData.won_appeals} approved appeal{roiData.won_appeals !== 1 ? 's' : ''}</p>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 p-5">
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">ROI Multiple</p>
                    <p className={`text-3xl font-extrabold ${roiData.roi_multiple >= 10 ? 'text-emerald-600' : roiData.roi_multiple >= 1 ? 'text-blue-600' : 'text-slate-400'}`}>
                      {roiData.roi_multiple > 0 ? `${roiData.roi_multiple}x` : '\u2014'}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      {roiData.roi_multiple > 0 ? `Return on $${roiData.subscription_cost}/mo subscription` : 'Add recovery amounts to see ROI'}
                    </p>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 p-5">
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Win Rate</p>
                    <p className={`text-3xl font-extrabold ${
                      roiData.win_rate_pct >= 70 ? 'text-emerald-600' : roiData.win_rate_pct >= 40 ? 'text-amber-600' : roiData.win_rate_pct > 0 ? 'text-red-600' : 'text-slate-300'
                    }`}>
                      {roiData.win_rate_pct > 0 ? `${roiData.win_rate_pct}%` : '\u2014'}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">{roiData.total_appeals} total appeal{roiData.total_appeals !== 1 ? 's' : ''}</p>
                  </div>
                  <div className="bg-white rounded-xl border border-blue-200 p-5">
                    <p className="text-xs font-bold text-blue-500 uppercase tracking-wider mb-1">Pipeline Value</p>
                    <p className="text-3xl font-extrabold text-blue-600">
                      {roiData.pending_value > 0 ? `$${roiData.pending_value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : '\u2014'}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">Pending/submitted claims</p>
                  </div>
                </div>

                {/* Summary stats row */}
                {roiData.total_claims_value > 0 && (
                  <div className="bg-white rounded-xl border border-slate-200 p-5">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                      <div>
                        <p className="text-xs font-bold text-slate-400 uppercase mb-1">Claims Value</p>
                        <p className="text-lg font-bold text-slate-700">${roiData.total_claims_value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</p>
                      </div>
                      <div>
                        <p className="text-xs font-bold text-slate-400 uppercase mb-1">Recovery Rate</p>
                        <p className="text-lg font-bold text-slate-700">{roiData.recovery_rate_pct}%</p>
                      </div>
                      <div>
                        <p className="text-xs font-bold text-slate-400 uppercase mb-1">Avg Recovery</p>
                        <p className="text-lg font-bold text-slate-700">${roiData.avg_recovery_per_appeal.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</p>
                      </div>
                      <div>
                        <p className="text-xs font-bold text-slate-400 uppercase mb-1">Won Appeals</p>
                        <p className="text-lg font-bold text-emerald-600">{roiData.won_appeals}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Recovery breakdown: top drugs and carriers */}
                {(roiData.top_recovered_drugs.length > 0 || roiData.top_recovered_carriers.length > 0) && (
                  <div className="grid md:grid-cols-2 gap-4">
                    {roiData.top_recovered_drugs.length > 0 && (
                      <div className="bg-white rounded-xl border border-slate-200 p-5">
                        <p className="text-xs font-bold text-emerald-500 uppercase tracking-wider mb-3">Top 5 Drugs by $ Recovered</p>
                        <div className="space-y-3">
                          {roiData.top_recovered_drugs.map((d, i) => {
                            const maxVal = roiData.top_recovered_drugs[0]?.recovered || 1
                            const pct = Math.round((d.recovered / maxVal) * 100)
                            return (
                              <div key={i}>
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-sm font-semibold text-slate-700 truncate">{d.drug}</span>
                                  <span className="text-sm font-bold text-emerald-600 ml-2">${d.recovered.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
                                </div>
                                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                  <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${pct}%` }} />
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )}
                    {roiData.top_recovered_carriers.length > 0 && (
                      <div className="bg-white rounded-xl border border-slate-200 p-5">
                        <p className="text-xs font-bold text-emerald-500 uppercase tracking-wider mb-3">Top 5 Carriers by $ Recovered</p>
                        <div className="space-y-3">
                          {roiData.top_recovered_carriers.map((c, i) => {
                            const maxVal = roiData.top_recovered_carriers[0]?.recovered || 1
                            const pct = Math.round((c.recovered / maxVal) * 100)
                            return (
                              <div key={i}>
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-sm font-semibold text-slate-700 truncate">{c.carrier}</span>
                                  <span className="text-sm font-bold text-emerald-600 ml-2">${c.recovered.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
                                </div>
                                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                  <div className="h-full bg-teal-500 rounded-full" style={{ width: `${pct}%` }} />
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Monthly trend */}
                {roiData.monthly_trend.some(m => m.recovered > 0) && (
                  <div className="bg-white rounded-xl border border-slate-200 p-5">
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">Monthly Recovery Trend</p>
                    <div className="flex items-end gap-3 h-40">
                      {(() => {
                        const maxRecovered = Math.max(...roiData.monthly_trend.map(m => m.recovered), 1)
                        return roiData.monthly_trend.map((m, i) => (
                          <div key={i} className="flex-1 flex flex-col items-center gap-1">
                            <span className="text-[10px] font-bold text-emerald-600">
                              {m.recovered > 0 ? `$${m.recovered >= 1000 ? `${(m.recovered / 1000).toFixed(1)}k` : m.recovered.toLocaleString()}` : ''}
                            </span>
                            <div className="w-full bg-slate-100 rounded-t-lg relative" style={{ height: '100%' }}>
                              <div
                                className="absolute bottom-0 left-0 right-0 bg-emerald-500 rounded-t-lg transition-all"
                                style={{ height: `${maxRecovered > 0 ? (m.recovered / maxRecovered) * 100 : 0}%`, minHeight: m.recovered > 0 ? '4px' : '0' }}
                              />
                            </div>
                            <span className="text-[10px] text-slate-400 font-medium">{m.label.split(' ')[0]}</span>
                          </div>
                        ))
                      })()}
                    </div>
                  </div>
                )}

                {/* Empty state for ROI */}
                {roiData.total_recovered === 0 && roiData.total_claims_value === 0 && (
                  <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                    <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-bold text-slate-800 mb-2">Start Tracking Revenue Recovery</h3>
                    <p className="text-sm text-slate-500 max-w-md mx-auto mb-4">
                      Add claim amounts and recovery values to your appeals to see how much money RxCheckUp is recovering for your practice.
                    </p>
                    <p className="text-xs text-slate-400">
                      Go to <button onClick={() => dispatchUI({ type: 'SET_ACTIVE_TAB', tab: 'appeals' })} className="text-teal-600 underline font-medium">My Appeals</button>, click View on any appeal, and enter the financial details.
                    </p>
                  </div>
                )}
              </>
            ) : (
              /* Fallback: compute ROI from local appeals data when API is unavailable */
              (() => {
                const appealsCount = dashboard?.total_lmns ?? appeals.length
                const approvedCount = dashboard?.approved ?? appeals.filter(a => a.outcome === 'approved').length
                const timeSavedMinutes = appealsCount * 30
                const timeSavedHours = timeSavedMinutes / 60
                const staffCostSaved = timeSavedHours * 35
                const estRevenueRecovered = approvedCount * 2500
                const subscriptionCost = (plan && PLAN_PRICES[plan]) ? PLAN_PRICES[plan] : 299
                const netROI = estRevenueRecovered - subscriptionCost
                const hasData = appealsCount > 0

                return hasData ? (
                  <div className="space-y-6">
                    {/* Monthly ROI Summary */}
                    <div className="bg-white rounded-xl border border-slate-200 p-6">
                      <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-5">Monthly ROI Summary</h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
                        <div className="bg-slate-50 rounded-xl p-4">
                          <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Time Saved</p>
                          <p className="text-2xl font-extrabold text-blue-600">{timeSavedHours.toFixed(1)}h</p>
                          <p className="text-xs text-slate-400 mt-1">{appealsCount} appeals x 30 min each</p>
                        </div>
                        <div className="bg-slate-50 rounded-xl p-4">
                          <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Staff Cost Saved</p>
                          <p className="text-2xl font-extrabold text-violet-600">${staffCostSaved.toLocaleString()}</p>
                          <p className="text-xs text-slate-400 mt-1">{timeSavedHours.toFixed(1)}h x $35/hr avg rate</p>
                        </div>
                        <div className="bg-slate-50 rounded-xl p-4">
                          <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Est. Revenue Recovered</p>
                          <p className="text-2xl font-extrabold text-emerald-600">${estRevenueRecovered.toLocaleString()}</p>
                          <p className="text-xs text-slate-400 mt-1">{approvedCount} won x $2,500 avg recovered</p>
                        </div>
                        <div className="bg-slate-50 rounded-xl p-4">
                          <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Net ROI</p>
                          <p className={`text-2xl font-extrabold ${netROI >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                            {netROI >= 0 ? '+' : ''}${netROI.toLocaleString()}
                          </p>
                          <p className="text-xs text-slate-400 mt-1">After ~${subscriptionCost}/mo subscription</p>
                        </div>
                      </div>
                    </div>

                    {/* ROI Visualization: subscription cost vs value delivered */}
                    <div className="bg-white rounded-xl border border-slate-200 p-6">
                      <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4">Subscription Cost vs Value Delivered</h3>
                      {(() => {
                        const totalValue = staffCostSaved + estRevenueRecovered
                        const maxBar = Math.max(subscriptionCost, totalValue, 1)
                        return (
                          <div className="space-y-4">
                            <div>
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-sm font-semibold text-slate-600">Subscription Cost</span>
                                <span className="text-sm font-bold text-slate-700">${subscriptionCost}/mo</span>
                              </div>
                              <div className="h-6 bg-slate-100 rounded-full overflow-hidden">
                                <div className="h-full bg-slate-400 rounded-full transition-all" style={{ width: `${(subscriptionCost / maxBar) * 100}%` }} />
                              </div>
                            </div>
                            <div>
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-sm font-semibold text-emerald-700">Value Delivered</span>
                                <span className="text-sm font-bold text-emerald-600">${totalValue.toLocaleString()}</span>
                              </div>
                              <div className="h-6 bg-emerald-50 rounded-full overflow-hidden">
                                <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${(totalValue / maxBar) * 100}%` }} />
                              </div>
                              <p className="text-xs text-slate-400 mt-1">Staff savings (${staffCostSaved.toLocaleString()}) + Revenue recovered (${estRevenueRecovered.toLocaleString()})</p>
                            </div>
                            {totalValue > 0 && (
                              <p className="text-sm font-semibold text-emerald-600 mt-2">
                                {(totalValue / subscriptionCost).toFixed(1)}x return on investment
                              </p>
                            )}
                          </div>
                        )
                      })()}
                    </div>
                  </div>
                ) : (
                  <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                    <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-bold text-slate-800 mb-2">Track Your ROI</h3>
                    <p className="text-sm text-slate-500 max-w-md mx-auto mb-4">
                      Track your first appeal outcome to see your ROI. Go to My Appeals to get started.
                    </p>
                    <button onClick={() => dispatchUI({ type: 'SET_ACTIVE_TAB', tab: 'appeals' })} className="text-sm text-teal-600 hover:text-teal-700 font-semibold underline">
                      Go to My Appeals
                    </button>
                  </div>
                )
              })()
            )}
          </div>
        )}

        {/* ── PRACTICE REPORT TAB ── */}
        {activeTab === 'report' && (
          <div className="space-y-6">
            {/* Period selector + Download */}
            <div className="flex items-center justify-between">
              <div className="flex gap-1 bg-white rounded-xl border border-slate-200 p-1">
                {[{ label: 'Month', value: 'month' }, { label: 'Quarter', value: 'quarter' }, { label: 'Year', value: 'year' }].map(opt => (
                  <button key={opt.value} onClick={() => dispatchFilters({ type: 'SET_REPORT_PERIOD', value: opt.value })}
                    className={'px-4 py-1.5 rounded-lg text-sm font-semibold transition-colors ' + (reportPeriod === opt.value ? 'bg-emerald-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50')}>
                    {opt.label}
                  </button>
                ))}
              </div>
              {practiceReport && (
                <button onClick={handleDownloadReport}
                  className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 transition-colors shadow-sm">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h12a2 2 0 002-2V7a2 2 0 00-2-2h-2M8 5a2 2 0 002 2h4a2 2 0 002-2M8 5a2 2 0 012-2h4a2 2 0 012 2" />
                  </svg>
                  Copy Report
                </button>
              )}
            </div>

            {reportLoading ? (
              <div className="text-center py-12 text-slate-400">Loading report...</div>
            ) : !practiceReport ? (
              <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                <h3 className="text-lg font-bold text-slate-800 mb-2">No Report Data</h3>
                <p className="text-sm text-slate-500">Generate appeals and track outcomes to build your practice report.</p>
              </div>
            ) : (
              <>
                {/* Report Title */}
                <div className="text-center mb-2">
                  <h2 className="text-xl font-extrabold text-slate-800">{practiceReport.report_title}</h2>
                  {practiceReport.practice_name && <p className="text-sm text-slate-500">{practiceReport.practice_name}</p>}
                  <p className="text-xs text-slate-400 mt-1">Generated {new Date(practiceReport.generated_at).toLocaleDateString()}</p>
                </div>

                {/* Executive Summary Card */}
                <div className="bg-gradient-to-br from-emerald-50 via-green-50 to-teal-50 border-2 border-emerald-200 rounded-2xl p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 rounded-lg bg-emerald-500 flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-extrabold text-emerald-900">Executive Summary</h3>
                  </div>

                  {/* Big recovered number */}
                  <div className="text-center mb-6">
                    <p className="text-4xl font-black text-emerald-700">
                      {'$' + practiceReport.executive_summary.total_recovered.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                    </p>
                    <p className="text-sm font-semibold text-emerald-600 mt-1">Total Recovered</p>
                    {practiceReport.executive_summary.roi_multiple > 0 && practiceReport.executive_summary.subscription_cost > 0 && (
                      <span className="inline-block mt-2 px-3 py-1 bg-emerald-600 text-white text-xs font-bold rounded-full">
                        {practiceReport.executive_summary.roi_multiple + 'x Return on $' + practiceReport.executive_summary.subscription_cost + '/mo subscription'}
                      </span>
                    )}
                  </div>

                  {/* Stats grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="bg-white/70 rounded-xl p-3 text-center">
                      <p className="text-2xl font-extrabold text-slate-800">{practiceReport.executive_summary.total_appeals_filed}</p>
                      <p className="text-[11px] font-bold text-slate-500 uppercase">Appeals Filed</p>
                    </div>
                    <div className="bg-white/70 rounded-xl p-3 text-center">
                      <p className="text-2xl font-extrabold text-emerald-600">{practiceReport.executive_summary.appeals_won}</p>
                      <p className="text-[11px] font-bold text-slate-500 uppercase">Won</p>
                    </div>
                    <div className="bg-white/70 rounded-xl p-3 text-center">
                      <p className={'text-2xl font-extrabold ' + (practiceReport.executive_summary.win_rate_pct >= 70 ? 'text-emerald-600' : practiceReport.executive_summary.win_rate_pct >= 50 ? 'text-amber-600' : 'text-red-600')}>
                        {practiceReport.executive_summary.win_rate_pct + '%'}
                      </p>
                      <p className="text-[11px] font-bold text-slate-500 uppercase">Win Rate</p>
                    </div>
                    <div className="bg-white/70 rounded-xl p-3 text-center">
                      <p className="text-2xl font-extrabold text-blue-600">{practiceReport.executive_summary.time_saved_hours + 'h'}</p>
                      <p className="text-[11px] font-bold text-slate-500 uppercase">Time Saved</p>
                    </div>
                  </div>

                  {/* Secondary stats */}
                  <div className="grid grid-cols-3 gap-3 mt-3">
                    <div className="bg-white/50 rounded-lg p-2 text-center">
                      <p className="text-sm font-bold text-slate-700">{'$' + practiceReport.executive_summary.total_claim_value.toLocaleString()}</p>
                      <p className="text-[10px] text-slate-500">Total Claim Value</p>
                    </div>
                    <div className="bg-white/50 rounded-lg p-2 text-center">
                      <p className="text-sm font-bold text-slate-700">{practiceReport.executive_summary.recovery_rate_pct + '%'}</p>
                      <p className="text-[10px] text-slate-500">Recovery Rate</p>
                    </div>
                    <div className="bg-white/50 rounded-lg p-2 text-center">
                      <p className="text-sm font-bold text-amber-700">{'$' + practiceReport.executive_summary.pending_pipeline_value.toLocaleString()}</p>
                      <p className="text-[10px] text-slate-500">Pipeline Value</p>
                    </div>
                  </div>

                  {practiceReport.executive_summary.total_claim_value === 0 && practiceReport.executive_summary.total_appeals_filed > 0 && (
                    <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
                      <p className="text-xs text-amber-700 font-medium">Enter claim amounts on your appeals to see recovery tracking</p>
                    </div>
                  )}
                </div>

                {/* Month-over-Month Changes */}
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <h4 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-3">Period-over-Period Changes</h4>
                  <div className="grid grid-cols-3 gap-4">
                    {[
                      { label: 'Appeals Volume', value: practiceReport.month_over_month.appeals_change_pct },
                      { label: 'Win Rate', value: practiceReport.month_over_month.win_rate_change_pct },
                      { label: 'Recovery', value: practiceReport.month_over_month.recovery_change_pct },
                    ].map(function(item) {
                      return (
                        <div key={item.label} className="text-center">
                          <p className={'text-xl font-bold ' + (item.value > 0 ? 'text-emerald-600' : item.value < 0 ? 'text-red-600' : 'text-slate-500')}>
                            {item.value > 0 ? '\u2191' : item.value < 0 ? '\u2193' : '\u2014'}
                            {' ' + Math.abs(item.value) + '%'}
                          </p>
                          <p className="text-[11px] font-semibold text-slate-500 uppercase">{item.label}</p>
                        </div>
                      )
                    })}
                  </div>
                </div>

                {/* Carrier Performance Table */}
                {practiceReport.carrier_breakdown.length > 0 && (
                  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                    <div className="px-5 py-4 border-b border-slate-100">
                      <h4 className="text-sm font-bold text-slate-700 uppercase tracking-wider">Carrier Performance</h4>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-slate-50 text-left">
                            <th className="px-5 py-2.5 text-[11px] font-bold text-slate-500 uppercase">Carrier</th>
                            <th className="px-3 py-2.5 text-[11px] font-bold text-slate-500 uppercase text-center">Appeals</th>
                            <th className="px-3 py-2.5 text-[11px] font-bold text-slate-500 uppercase text-center">Won</th>
                            <th className="px-3 py-2.5 text-[11px] font-bold text-slate-500 uppercase text-center">Win Rate</th>
                            <th className="px-3 py-2.5 text-[11px] font-bold text-slate-500 uppercase text-right">Recovered</th>
                            <th className="px-3 py-2.5 text-[11px] font-bold text-slate-500 uppercase text-center">Avg Days</th>
                          </tr>
                        </thead>
                        <tbody>
                          {practiceReport.carrier_breakdown.map(function(c) {
                            const winColor = c.win_rate >= 70 ? 'text-emerald-600 bg-emerald-50' : c.win_rate >= 50 ? 'text-amber-600 bg-amber-50' : 'text-red-600 bg-red-50'
                            return (
                              <tr key={c.carrier} className="border-t border-slate-100 hover:bg-slate-50">
                                <td className="px-5 py-3 font-semibold text-slate-800">{c.carrier}</td>
                                <td className="px-3 py-3 text-center text-slate-600">{c.appeals}</td>
                                <td className="px-3 py-3 text-center text-slate-600">{c.won}</td>
                                <td className="px-3 py-3 text-center">
                                  <span className={'text-xs font-bold px-2 py-0.5 rounded-full ' + winColor}>{c.win_rate + '%'}</span>
                                </td>
                                <td className="px-3 py-3 text-right font-semibold text-emerald-700">{'$' + c.recovered.toLocaleString()}</td>
                                <td className="px-3 py-3 text-center text-slate-500">{c.avg_days_to_resolution != null ? c.avg_days_to_resolution + 'd' : '-'}</td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Drug Performance Table */}
                {practiceReport.drug_breakdown.length > 0 && (
                  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                    <div className="px-5 py-4 border-b border-slate-100">
                      <h4 className="text-sm font-bold text-slate-700 uppercase tracking-wider">Drug Performance</h4>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-slate-50 text-left">
                            <th className="px-5 py-2.5 text-[11px] font-bold text-slate-500 uppercase">Drug</th>
                            <th className="px-3 py-2.5 text-[11px] font-bold text-slate-500 uppercase text-center">Appeals</th>
                            <th className="px-3 py-2.5 text-[11px] font-bold text-slate-500 uppercase text-center">Won</th>
                            <th className="px-3 py-2.5 text-[11px] font-bold text-slate-500 uppercase text-center">Win Rate</th>
                            <th className="px-3 py-2.5 text-[11px] font-bold text-slate-500 uppercase text-right">Recovered</th>
                          </tr>
                        </thead>
                        <tbody>
                          {practiceReport.drug_breakdown.map(function(d) {
                            const winColor = d.win_rate >= 70 ? 'text-emerald-600 bg-emerald-50' : d.win_rate >= 50 ? 'text-amber-600 bg-amber-50' : 'text-red-600 bg-red-50'
                            return (
                              <tr key={d.drug} className="border-t border-slate-100 hover:bg-slate-50">
                                <td className="px-5 py-3 font-semibold text-slate-800">{d.drug}</td>
                                <td className="px-3 py-3 text-center text-slate-600">{d.appeals}</td>
                                <td className="px-3 py-3 text-center text-slate-600">{d.won}</td>
                                <td className="px-3 py-3 text-center">
                                  <span className={'text-xs font-bold px-2 py-0.5 rounded-full ' + winColor}>{d.win_rate + '%'}</span>
                                </td>
                                <td className="px-3 py-3 text-right font-semibold text-emerald-700">{'$' + d.recovered.toLocaleString()}</td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Staff Performance Table (team owners only) */}
                {practiceReport.staff_performance.length > 0 && (
                  <div className="bg-white rounded-xl border border-indigo-200 overflow-hidden">
                    <div className="px-5 py-4 border-b border-indigo-100 bg-indigo-50">
                      <h4 className="text-sm font-bold text-indigo-700 uppercase tracking-wider">Staff Performance</h4>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-slate-50 text-left">
                            <th className="px-5 py-2.5 text-[11px] font-bold text-slate-500 uppercase">Staff Member</th>
                            <th className="px-3 py-2.5 text-[11px] font-bold text-slate-500 uppercase text-center">Handled</th>
                            <th className="px-3 py-2.5 text-[11px] font-bold text-slate-500 uppercase text-center">Won</th>
                            <th className="px-3 py-2.5 text-[11px] font-bold text-slate-500 uppercase text-center">Win Rate</th>
                            <th className="px-3 py-2.5 text-[11px] font-bold text-slate-500 uppercase text-right">Recovered</th>
                          </tr>
                        </thead>
                        <tbody>
                          {practiceReport.staff_performance.map(function(sp) {
                            const winColor = sp.win_rate >= 70 ? 'text-emerald-600 bg-emerald-50' : sp.win_rate >= 50 ? 'text-amber-600 bg-amber-50' : 'text-red-600 bg-red-50'
                            return (
                              <tr key={sp.name} className="border-t border-slate-100 hover:bg-slate-50">
                                <td className="px-5 py-3 font-semibold text-slate-800">{sp.name}</td>
                                <td className="px-3 py-3 text-center text-slate-600">{sp.appeals_handled}</td>
                                <td className="px-3 py-3 text-center text-slate-600">{sp.won}</td>
                                <td className="px-3 py-3 text-center">
                                  <span className={'text-xs font-bold px-2 py-0.5 rounded-full ' + winColor}>{sp.win_rate + '%'}</span>
                                </td>
                                <td className="px-3 py-3 text-right font-semibold text-emerald-700">{'$' + sp.recovered.toLocaleString()}</td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Key Highlights */}
                {practiceReport.highlights.length > 0 && (
                  <div className="bg-gradient-to-r from-slate-50 to-slate-100 border border-slate-200 rounded-xl p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <svg className="w-5 h-5 text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                      </svg>
                      <h4 className="text-sm font-bold text-slate-700 uppercase tracking-wider">Key Highlights</h4>
                    </div>
                    <ul className="space-y-2">
                      {practiceReport.highlights.map(function(h, i) {
                        return (
                          <li key={i} className="flex items-start gap-2.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 flex-shrink-0"></span>
                            <span className="text-sm text-slate-700 leading-relaxed">{h}</span>
                          </li>
                        )
                      })}
                    </ul>
                  </div>
                )}

                {/* Print / Download footer */}
                <div className="text-center py-4">
                  <button onClick={handleDownloadReport}
                    className="px-6 py-2.5 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 transition-colors shadow-sm">
                    Copy Full Report to Clipboard
                  </button>
                  <button onClick={function() { window.print() }}
                    className="ml-3 px-6 py-2.5 bg-white text-slate-700 border border-slate-300 rounded-lg text-sm font-semibold hover:bg-slate-50 transition-colors">
                    Print Report
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* ── INSIGHTS TAB ── */}
        {activeTab === 'insights' && (
          <div className="space-y-4">
            <div className="bg-gradient-to-r from-violet-50 to-indigo-50 border border-violet-200 rounded-xl p-5">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-violet-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-bold text-violet-800 mb-1">How Learning Works</h3>
                  <p className="text-sm text-violet-600">
                    When you mark an appeal as denied and provide feedback about the payer's reason,
                    RxCheckUp learns from that experience. Next time you generate an LMN for the same
                    carrier or drug, the system incorporates those insights to create a stronger letter.
                    <span className="font-semibold"> All data stays within your account — no patient information
                    is ever shared between users.</span>
                  </p>
                </div>
              </div>
            </div>

            {insightsLoading ? (
              <div className="text-center py-8 text-slate-400">Loading insights...</div>
            ) : insights.length === 0 ? (
              <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                <div className="w-14 h-14 rounded-full bg-violet-50 flex items-center justify-center mx-auto mb-4">
                  <svg className="w-7 h-7 text-violet-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
                  </svg>
                </div>
                <h3 className="text-lg font-bold text-slate-800 mb-2">No Insights Yet</h3>
                <p className="text-sm text-slate-500 max-w-md mx-auto">
                  When appeals are denied, submit feedback about the payer's reason.
                  RxCheckUp will learn from each denial and improve future LMNs automatically.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {insights.map(insight => (
                  <div key={insight.id} className="bg-white rounded-xl border border-slate-200 p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="font-semibold text-slate-800">{insight.denied_product}</p>
                        <p className="text-xs text-slate-400">{insight.carrier} &bull; {new Date(insight.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="bg-red-50 rounded-lg p-3">
                        <p className="text-xs font-bold text-red-400 uppercase mb-1">Payer's Reason</p>
                        <p className="text-sm text-red-700">{insight.payer_reason}</p>
                      </div>
                      {insight.lesson_learned && (
                        <div className="bg-violet-50 rounded-lg p-3">
                          <p className="text-xs font-bold text-violet-400 uppercase mb-1">Lesson Learned</p>
                          <p className="text-sm text-violet-700">{insight.lesson_learned}</p>
                        </div>
                      )}
                      {insight.should_include_next_time && (
                        <div className="bg-emerald-50 rounded-lg p-3">
                          <p className="text-xs font-bold text-emerald-400 uppercase mb-1">Include in Future LMNs</p>
                          <p className="text-sm text-emerald-700">{insight.should_include_next_time}</p>
                        </div>
                      )}
                      {insight.payer_requirements && (
                        <div className="bg-blue-50 rounded-lg p-3">
                          <p className="text-xs font-bold text-blue-400 uppercase mb-1">Payer Requirements</p>
                          <p className="text-sm text-blue-700">{insight.payer_requirements}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── TEAM TAB ── */}
        {activeTab === 'team' && isTeamOwner && (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-indigo-50 to-violet-50 border border-indigo-200 rounded-xl p-5">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-bold text-indigo-800 mb-1">Team Workload</h3>
                  <p className="text-sm text-indigo-600">
                    View and manage appeal assignments across your team. Assign appeals from the detail view of any appeal.
                  </p>
                </div>
              </div>
            </div>

            {staffWorkloadLoading ? (
              <div className="text-center py-12">
                <div className="w-8 h-8 border-3 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                <p className="text-sm text-slate-400">Loading team workload...</p>
              </div>
            ) : staffWorkload.length === 0 ? (
              <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                <h3 className="text-lg font-bold text-slate-700 mb-1">No Workload Data</h3>
                <p className="text-sm text-slate-400">Assign appeals to team members to see workload data here.</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {(() => {
                  const avgAssigned = staffWorkload.length > 0
                    ? staffWorkload.reduce((sum, s) => sum + s.total_assigned, 0) / staffWorkload.length
                    : 1
                  const maxAssigned = Math.max(...staffWorkload.map(s => s.total_assigned), 1)
                  return staffWorkload.map(staff => (
                    <div key={staff.id} className="bg-white rounded-xl border border-slate-200 p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className="font-bold text-slate-800">{staff.name}</h4>
                          <p className="text-xs text-slate-400">{staff.email}</p>
                        </div>
                        {staff.upcoming_deadlines > 0 && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-semibold">
                            {staff.upcoming_deadlines} deadline{staff.upcoming_deadlines !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>

                      {/* Workload bar */}
                      <div className="mb-3">
                        <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                          <span>Active Appeals</span>
                          <span className="font-bold text-slate-700">{staff.total_assigned}</span>
                        </div>
                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${
                              staff.total_assigned > avgAssigned * 1.5 ? 'bg-red-500' :
                              staff.total_assigned > avgAssigned ? 'bg-amber-500' :
                              'bg-indigo-500'
                            }`}
                            style={{ width: `${Math.min((staff.total_assigned / maxAssigned) * 100, 100)}%` }}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3 text-xs">
                        <div>
                          <p className="text-slate-400">Win Rate</p>
                          <p className={`font-bold ${
                            staff.win_rate === null ? 'text-slate-300' :
                            staff.win_rate >= 70 ? 'text-emerald-600' :
                            staff.win_rate >= 40 ? 'text-amber-600' : 'text-red-600'
                          }`}>
                            {staff.win_rate !== null ? `${staff.win_rate}%` : '--'}
                          </p>
                        </div>
                        <div>
                          <p className="text-slate-400">Total Recovered</p>
                          <p className="font-bold text-emerald-600">
                            {staff.total_recovered > 0 ? `$${staff.total_recovered.toLocaleString()}` : '--'}
                          </p>
                        </div>
                        <div>
                          <p className="text-slate-400">Pending</p>
                          <p className="font-semibold text-slate-700">{staff.pending_count}</p>
                        </div>
                        <div>
                          <p className="text-slate-400">Submitted</p>
                          <p className="font-semibold text-blue-600">{staff.submitted_count}</p>
                        </div>
                        <div>
                          <p className="text-slate-400">Approved</p>
                          <p className="font-semibold text-emerald-600">{staff.approved_count}</p>
                        </div>
                        <div>
                          <p className="text-slate-400">Denied</p>
                          <p className="font-semibold text-red-600">{staff.denied_count}</p>
                        </div>
                      </div>
                    </div>
                  ))
                })()}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Appeal Detail Modal ── */}
      {selectedAppeal && (
        <div className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm flex items-center justify-center">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[85vh] overflow-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <div>
                <h3 className="font-bold text-slate-800">{selectedAppeal.denied_product}</h3>
                <p className="text-xs text-slate-400">{selectedAppeal.carrier} &bull; {selectedAppeal.denial_reason.replace(/_/g, ' ')}</p>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${OUTCOME_STYLES[selectedAppeal.outcome]}`}>
                  {OUTCOME_LABELS[selectedAppeal.outcome]}
                </span>
                <button onClick={() => dispatchData({ type: 'SET_SELECTED_APPEAL', value: null })} className="text-slate-400 hover:text-slate-600">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              {/* ── Appeal Stage Timeline ── */}
              {timelineLoading ? (
                <div className="flex items-center gap-2 py-4 text-sm text-slate-400">
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                  Loading appeal timeline...
                </div>
              ) : timelineData && timelineData.stages.length > 0 ? (
                <div className="bg-slate-50 rounded-xl border border-slate-200 p-4">
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Appeal Progress</p>
                  <div className="relative">
                    {(() => {
                      const visibleStages = timelineData.stages.filter(
                        s => !['resolved_approved', 'resolved_denied'].includes(s.id) || s.status === 'current'
                      )
                      return (
                        <div className="flex items-center gap-0 overflow-x-auto pb-2">
                          {visibleStages.map((stage, idx) => (
                            <div key={stage.id} className="flex items-center flex-shrink-0">
                              <button
                                onClick={() => {
                                  if (stage.events.length > 0) dispatchData({ type: 'SET_SELECTED_TIMELINE_EVENT', value: stage.events[stage.events.length - 1] })
                                  else dispatchData({ type: 'SET_SELECTED_TIMELINE_EVENT', value: null })
                                }}
                                className={`relative flex items-center justify-center w-8 h-8 rounded-full border-2 transition-all cursor-pointer ${
                                  stage.status === 'completed' ? 'bg-emerald-500 border-emerald-500 text-white'
                                  : stage.status === 'current'
                                    ? stage.id === 'resolved_approved' ? 'bg-emerald-500 border-emerald-500 text-white'
                                      : stage.id === 'resolved_denied' ? 'bg-red-500 border-red-500 text-white'
                                      : 'bg-blue-500 border-blue-500 text-white animate-pulse'
                                    : 'bg-white border-slate-300 text-slate-400'
                                }`}
                                title={`${stage.label}: ${stage.description}`}
                              >
                                {stage.status === 'completed' ? (
                                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                                ) : stage.status === 'current' && (stage.id === 'resolved_approved') ? (
                                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                                ) : stage.status === 'current' && stage.id === 'resolved_denied' ? (
                                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                                ) : (
                                  <span className="text-[10px] font-bold">{idx + 1}</span>
                                )}
                              </button>
                              {idx < visibleStages.length - 1 && (
                                <div className={`w-8 h-0.5 ${stage.status === 'completed' ? 'bg-emerald-400' : 'bg-slate-200'}`} />
                              )}
                            </div>
                          ))}
                        </div>
                      )
                    })()}
                    {(() => {
                      const visibleStages = timelineData.stages.filter(
                        s => !['resolved_approved', 'resolved_denied'].includes(s.id) || s.status === 'current'
                      )
                      return (
                        <div className="flex gap-0 overflow-x-auto mt-1">
                          {visibleStages.map((stage, idx) => (
                            <div key={stage.id} className="flex items-center flex-shrink-0" style={{ width: idx < visibleStages.length - 1 ? 'calc(2rem + 2rem)' : '2rem' }}>
                              <p className={`text-[10px] leading-tight text-center w-16 -ml-4 ${
                                stage.status === 'current' ? 'font-bold text-blue-700' : stage.status === 'completed' ? 'font-semibold text-emerald-700' : 'text-slate-400'
                              }`}>{stage.label}</p>
                            </div>
                          ))}
                        </div>
                      )
                    })()}
                  </div>
                  {selectedTimelineEvent && (
                    <div className="mt-3 bg-white rounded-lg border border-slate-200 p-3">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-xs font-bold text-slate-700">{selectedTimelineEvent.stage_label}</p>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                          selectedTimelineEvent.action === 'approved' ? 'bg-emerald-100 text-emerald-700'
                          : selectedTimelineEvent.action === 'denied' ? 'bg-red-100 text-red-700'
                          : selectedTimelineEvent.action === 'escalated' ? 'bg-amber-100 text-amber-700'
                          : 'bg-blue-100 text-blue-700'
                        }`}>{selectedTimelineEvent.action}</span>
                      </div>
                      {selectedTimelineEvent.notes && <p className="text-xs text-slate-600">{selectedTimelineEvent.notes}</p>}
                      {selectedTimelineEvent.created_at && <p className="text-[10px] text-slate-400 mt-1">{new Date(selectedTimelineEvent.created_at).toLocaleString()}</p>}
                    </div>
                  )}
                  {timelineData.current_stage_label && (
                    <p className="text-xs text-slate-500 mt-2">Current stage: <span className="font-bold text-blue-700">{timelineData.current_stage_label}</span></p>
                  )}
                </div>
              ) : null}

              {/* ── Next Steps Card ── */}
              {nextStepsData && !nextStepsData.is_resolved && nextStepsData.guidance && (
                <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-bold text-blue-800 mb-1">Next Steps</p>
                      <p className="text-sm font-semibold text-blue-700 mb-1">{nextStepsData.guidance.next_action}</p>
                      <p className="text-xs text-blue-600 mb-2">{nextStepsData.guidance.guidance}</p>
                      {nextStepsData.guidance.deadline_days && (
                        <p className="text-xs font-semibold text-amber-700 bg-amber-50 inline-block px-2 py-0.5 rounded-md mb-2">
                          Filing deadline: {nextStepsData.guidance.deadline_days} days
                        </p>
                      )}
                      {nextStepsData.guidance.tips.length > 0 && (
                        <div className="mt-2">
                          <p className="text-[10px] font-bold text-blue-400 uppercase mb-1">Tips</p>
                          <ul className="space-y-1">
                            {nextStepsData.guidance.tips.map((tip, i) => (
                              <li key={i} className="text-xs text-blue-600 flex items-start gap-1.5"><span className="text-blue-400 mt-0.5">&#8226;</span>{tip}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                  {nextStepsData.checklist.length > 0 && (
                    <div className="mt-3 bg-white/60 rounded-lg p-3">
                      <p className="text-[10px] font-bold text-slate-500 uppercase mb-2">Escalation Checklist</p>
                      <div className="space-y-1.5">
                        {nextStepsData.checklist.map(item => (
                          <label key={item.id} className="flex items-start gap-2 text-xs">
                            <input type="checkbox" className="mt-0.5 rounded border-slate-300 text-blue-500 focus:ring-blue-500/30" />
                            <div>
                              <span className={`font-semibold ${item.required ? 'text-slate-700' : 'text-slate-500'}`}>
                                {item.label}{item.required && <span className="text-red-400 ml-0.5">*</span>}
                              </span>
                              <p className="text-[10px] text-slate-400">{item.description}</p>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="mt-3 flex gap-2">
                    <button onClick={() => { dispatchUI({ type: 'SET_SHOW_ADVANCE_MODAL', value: true }); dispatchForms({ type: 'SET_ADVANCE_NOTES', value: '' }); dispatchData({ type: 'SET_ADVANCE_RESULT', value: null }) }}
                      className="px-4 py-2 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700 transition-colors">
                      Record Stage Result
                    </button>
                  </div>
                </div>
              )}

              {/* Advance result notification */}
              {advanceResult && (
                <div className={`rounded-xl border p-4 ${
                  advanceResult.action === 'approved' ? 'bg-emerald-50 border-emerald-200'
                  : advanceResult.action === 'exhausted' ? 'bg-red-50 border-red-200'
                  : 'bg-amber-50 border-amber-200'
                }`}>
                  <p className={`text-sm font-bold mb-1 ${
                    advanceResult.action === 'approved' ? 'text-emerald-800'
                    : advanceResult.action === 'exhausted' ? 'text-red-800' : 'text-amber-800'
                  }`}>{advanceResult.message}</p>
                  {advanceResult.new_stage_label && (
                    <p className="text-xs text-slate-600">New stage: <span className="font-semibold">{advanceResult.new_stage_label}</span></p>
                  )}
                  <button onClick={() => dispatchData({ type: 'SET_ADVANCE_RESULT', value: null })} className="text-xs text-slate-500 hover:text-slate-700 mt-2 underline">Dismiss</button>
                </div>
              )}

              {/* Deadline tracking in detail view */}
              {(selectedAppeal as any).appeal_deadline && (
                <div className="bg-slate-50 rounded-xl border border-slate-200 p-4">
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Deadline Tracking</p>
                  <div className="flex items-center gap-6">
                    <div>
                      <p className="text-xs text-slate-500">Appeal Level</p>
                      <p className="text-sm font-semibold text-slate-700">{(selectedAppeal as any).appeal_level_label || 'First-Level Appeal'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Filing Deadline</p>
                      <p className="text-sm font-semibold text-slate-700">
                        {new Date((selectedAppeal as any).appeal_deadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </p>
                    </div>
                    {(selectedAppeal as any).deadline_status && (
                      <div>
                        <p className="text-xs text-slate-500">Status</p>
                        <p className={`text-sm font-bold ${
                          (selectedAppeal as any).deadline_status.color === 'red' ? 'text-red-600' :
                          (selectedAppeal as any).deadline_status.color === 'amber' ? 'text-amber-600' :
                          'text-emerald-600'
                        }`}>
                          {(selectedAppeal as any).deadline_status.label}
                        </p>
                      </div>
                    )}
                    {(selectedAppeal as any).denial_date && (
                      <div>
                        <p className="text-xs text-slate-500">Denial Date</p>
                        <p className="text-sm font-semibold text-slate-700">
                          {new Date((selectedAppeal as any).denial_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </p>
                      </div>
                    )}
                    {(selectedAppeal as any).appeal_submitted_date && (
                      <div>
                        <p className="text-xs text-slate-500">Submitted</p>
                        <p className="text-sm font-semibold text-emerald-600">
                          {new Date((selectedAppeal as any).appeal_submitted_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </p>
                      </div>
                    )}
                  </div>
                  {/* Timeline visualization */}
                  {(selectedAppeal as any).denial_date && (selectedAppeal as any).appeal_deadline && (
                    <div className="mt-3 pt-3 border-t border-slate-200">
                      <div className="relative h-3 bg-slate-200 rounded-full overflow-hidden">
                        {(() => {
                          const denial = new Date((selectedAppeal as any).denial_date).getTime()
                          const deadline = new Date((selectedAppeal as any).appeal_deadline).getTime()
                          const now = Date.now()
                          const total = deadline - denial
                          const elapsed = now - denial
                          const pct = total > 0 ? Math.min(Math.max((elapsed / total) * 100, 0), 100) : 0
                          const color = pct >= 90 ? 'bg-red-500' : pct >= 75 ? 'bg-amber-500' : 'bg-emerald-500'
                          return <div className={`absolute inset-y-0 left-0 rounded-full ${color}`} style={{ width: `${pct}%` }} />
                        })()}
                      </div>
                      <div className="flex justify-between text-[10px] text-slate-400 mt-1">
                        <span>Denial received</span>
                        <span>Deadline</span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Reference tag in detail view */}
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-xs font-bold text-slate-400 uppercase">Reference Tag</p>
                  <div className="group relative">
                    <svg className="w-3.5 h-3.5 text-slate-400 cursor-help" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
                    </svg>
                    <div className="absolute bottom-full left-0 mb-2 w-64 p-2.5 bg-slate-800 text-white text-xs rounded-lg shadow-lg opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity z-10">
                      Use reference tags to organize your letters. Avoid using patient names or identifiers — use initials, case numbers, or your own shorthand instead.
                    </div>
                  </div>
                </div>
                {editingTagId === selectedAppeal.id ? (
                  <div>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={editingTagValue}
                        onChange={e => dispatchForms({ type: 'SET_EDITING_TAG_VALUE', value: e.target.value })}
                        onKeyDown={e => { if (e.key === 'Enter') handleSaveTag(selectedAppeal.id); if (e.key === 'Escape') dispatchForms({ type: 'SET_EDITING_TAG', id: null, value: '' }) }}
                        maxLength={100}
                        placeholder="Add a reference tag (e.g., Case-Humira-April)"
                        className="flex-1 px-3 py-2 border border-teal-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/30"
                        autoFocus
                      />
                      <button onClick={() => handleSaveTag(selectedAppeal.id)} disabled={savingTag}
                        className="px-3 py-2 bg-teal-500 text-white text-xs font-semibold rounded-lg hover:bg-teal-600 disabled:opacity-50">
                        Save
                      </button>
                      <button onClick={() => dispatchForms({ type: 'SET_EDITING_TAG', id: null, value: '' })}
                        className="px-3 py-2 border border-slate-200 text-slate-500 text-xs font-semibold rounded-lg hover:bg-slate-50">
                        Cancel
                      </button>
                    </div>
                    <p className="text-[11px] text-amber-600 mt-1">&#9888; Do not include patient names or initials. Use non-identifying labels only (e.g., 'Case-Humira-April').</p>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    {selectedAppeal.reference_tag ? (
                      <span className="text-sm font-semibold px-3 py-1 rounded-full bg-teal-50 text-teal-700 border border-teal-200">
                        {selectedAppeal.reference_tag}
                      </span>
                    ) : (
                      <span className="text-sm text-slate-400 italic">No tag set</span>
                    )}
                    <button onClick={() => dispatchForms({ type: 'SET_EDITING_TAG', id: selectedAppeal.id, value: selectedAppeal.reference_tag || '' })}
                      className="text-xs text-teal-600 hover:text-teal-800 font-semibold">
                      {selectedAppeal.reference_tag ? 'Edit' : 'Add Tag'}
                    </button>
                  </div>
                )}
                <p className="text-[11px] text-amber-600 mt-1">&#9888; Do not include patient names or initials. Use non-identifying labels only (e.g., 'Case-Humira-April').</p>
              </div>

              {/* Financial tracking */}
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-bold text-emerald-600 uppercase tracking-wider">Revenue Tracking</p>
                  {!editingFinancials && (
                    <button onClick={startEditingFinancials}
                      className="text-xs text-emerald-600 hover:text-emerald-800 font-semibold">
                      {(selectedAppeal.claim_amount || selectedAppeal.recovered_amount || selectedAppeal.estimated_annual_value) ? 'Edit' : 'Add Financial Data'}
                    </button>
                  )}
                </div>
                {editingFinancials ? (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">Claim Amount ($)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={finClaimAmount}
                        onChange={e => dispatchForms({ type: 'SET_FIN_CLAIM_AMOUNT', value: e.target.value })}
                        placeholder="Amount denied by payer"
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">
                        Recovered Amount ($)
                        {selectedAppeal.outcome !== 'approved' && (
                          <span className="text-slate-400 font-normal ml-1">(set when approved)</span>
                        )}
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={finRecoveredAmount}
                        onChange={e => dispatchForms({ type: 'SET_FIN_RECOVERED_AMOUNT', value: e.target.value })}
                        placeholder="Amount recovered after appeal"
                        disabled={selectedAppeal.outcome !== 'approved'}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400 disabled:bg-slate-100 disabled:text-slate-400"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">Estimated Annual Value ($)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={finAnnualValue}
                        onChange={e => dispatchForms({ type: 'SET_FIN_ANNUAL_VALUE', value: e.target.value })}
                        placeholder="For recurring medications"
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400"
                      />
                    </div>
                    <div className="flex gap-2 pt-1">
                      <button onClick={handleSaveFinancials} disabled={savingFinancials}
                        className="px-4 py-2 bg-emerald-600 text-white text-xs font-semibold rounded-lg hover:bg-emerald-700 disabled:opacity-50">
                        {savingFinancials ? 'Saving...' : 'Save'}
                      </button>
                      <button onClick={() => dispatchUI({ type: 'SET_EDITING_FINANCIALS', value: false })}
                        className="px-4 py-2 border border-slate-200 text-slate-500 text-xs font-semibold rounded-lg hover:bg-slate-50">
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-slate-500">Claim Amount</p>
                      <p className="text-sm font-bold text-slate-700">
                        {selectedAppeal.claim_amount ? `$${selectedAppeal.claim_amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '\u2014'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Recovered</p>
                      <p className={`text-sm font-bold ${selectedAppeal.recovered_amount ? 'text-emerald-600' : 'text-slate-400'}`}>
                        {selectedAppeal.recovered_amount ? `$${selectedAppeal.recovered_amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '\u2014'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Annual Value</p>
                      <p className="text-sm font-bold text-slate-700">
                        {selectedAppeal.estimated_annual_value ? `$${selectedAppeal.estimated_annual_value.toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '\u2014'}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Action buttons based on state */}
              <div className="flex gap-2 flex-wrap">
                {selectedAppeal.outcome === 'pending' && (
                  <button onClick={() => { handleUpdateOutcome(selectedAppeal.id, 'submitted'); dispatchData({ type: 'UPDATE_SELECTED_APPEAL', updater: a => a ? {...a, outcome: 'submitted'} : null }) }}
                    className="px-4 py-2 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700">
                    Mark as Submitted to Payer
                  </button>
                )}
                {selectedAppeal.outcome === 'submitted' && (
                  <>
                    <button onClick={() => { handleUpdateOutcome(selectedAppeal.id, 'approved'); dispatchData({ type: 'UPDATE_SELECTED_APPEAL', updater: a => a ? {...a, outcome: 'approved'} : null }) }}
                      className="px-4 py-2 bg-emerald-600 text-white text-xs font-semibold rounded-lg hover:bg-emerald-700">
                      Appeal Approved
                    </button>
                    <button onClick={() => { handleUpdateOutcome(selectedAppeal.id, 'denied'); dispatchData({ type: 'UPDATE_SELECTED_APPEAL', updater: a => a ? {...a, outcome: 'denied'} : null }) }}
                      className="px-4 py-2 bg-red-600 text-white text-xs font-semibold rounded-lg hover:bg-red-700">
                      Appeal Denied
                    </button>
                  </>
                )}
                {selectedAppeal.outcome === 'denied' && (
                  <button onClick={() => openFeedbackForm(selectedAppeal.id)}
                    className="px-4 py-2 bg-violet-600 text-white text-xs font-semibold rounded-lg hover:bg-violet-700">
                    Report Denial Reason &amp; Help Improve Future LMNs
                  </button>
                )}
                <button onClick={() => handleGenerateP2P(selectedAppeal.id)}
                  disabled={p2pLoading}
                  className="px-4 py-2 bg-orange-500 text-white text-xs font-semibold rounded-lg hover:bg-orange-600 disabled:opacity-50 flex items-center gap-1.5">
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
                  </svg>
                  {p2pLoading ? 'Generating...' : 'Prepare for Peer-to-Peer'}
                </button>
              </div>

              {/* Staff assignment (team owners only) */}
              {isTeamOwner && (
                <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4">
                  <p className="text-xs font-bold text-indigo-400 uppercase tracking-wider mb-2">Assigned To</p>
                  <select
                    value={(selectedAppeal as any)?.assigned_to_id || ''}
                    onChange={e => handleAssignAppeal(selectedAppeal.id, e.target.value || null)}
                    disabled={assigningAppeal === selectedAppeal.id}
                    className="w-full px-3 py-2 border border-indigo-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-400 bg-white disabled:opacity-50"
                  >
                    <option value="">Unassigned</option>
                    {teamMembers.map(m => (
                      <option key={m.id} value={m.id}>{m.name} ({m.email})</option>
                    ))}
                  </select>
                  {(selectedAppeal as any)?.assigned_to_name && (
                    <p className="text-xs text-indigo-600 mt-1">Currently assigned to: <span className="font-semibold">{(selectedAppeal as any).assigned_to_name}</span></p>
                  )}
                </div>
              )}

              {/* ICD-10 codes */}
              <div>
                <p className="text-xs font-bold text-slate-400 uppercase mb-1">Diagnosis Codes</p>
                <div className="flex gap-1.5 flex-wrap">
                  {selectedAppeal.icd10_codes.map((code, i) => (
                    <span key={i} className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-md font-mono">{code}</span>
                  ))}
                </div>
              </div>

              {/* Carrier Quick Contact — auto-filled from payer directory */}
              {carrierContact && (
                <div className="bg-teal-50 border border-teal-200 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-bold text-teal-600 uppercase">Carrier Contact — {carrierContact.carrier_name}</p>
                    <span className="text-[10px] text-teal-500">{carrierContact.parent_company}</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {carrierContact.appeals_fax && !carrierContact.appeals_fax.startsWith('Varies') && !carrierContact.appeals_fax.startsWith('N/A') && (
                      <button onClick={() => copyCarrierContact(carrierContact!.appeals_fax, 'Appeals fax')}
                        className="flex items-center gap-1 text-[11px] font-semibold bg-white text-red-600 border border-red-200 rounded-full px-2.5 py-1 hover:bg-red-50 transition-colors">
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0110.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0l.229 2.523a1.125 1.125 0 01-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0021 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 00-1.913-.247M6.34 18H5.25A2.25 2.25 0 013 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 011.913-.247m10.5 0a48.536 48.536 0 00-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659M18.75 9H5.25" />
                        </svg>
                        Fax: {carrierContact.appeals_fax}
                      </button>
                    )}
                    {carrierContact.appeals_phone && !carrierContact.appeals_phone.startsWith('Varies') && !carrierContact.appeals_phone.startsWith('N/A') && (
                      <button onClick={() => copyCarrierContact(carrierContact!.appeals_phone, 'Appeals phone')}
                        className="flex items-center gap-1 text-[11px] font-semibold bg-white text-blue-600 border border-blue-200 rounded-full px-2.5 py-1 hover:bg-blue-50 transition-colors">
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
                        </svg>
                        Phone: {carrierContact.appeals_phone}
                      </button>
                    )}
                    {carrierContact.peer_to_peer_phone && !carrierContact.peer_to_peer_phone.startsWith('Varies') && (
                      <button onClick={() => copyCarrierContact(carrierContact!.peer_to_peer_phone, 'P2P phone')}
                        className="flex items-center gap-1 text-[11px] font-semibold bg-white text-violet-600 border border-violet-200 rounded-full px-2.5 py-1 hover:bg-violet-50 transition-colors">
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
                        </svg>
                        P2P: {carrierContact.peer_to_peer_phone}
                      </button>
                    )}
                  </div>
                  {carrierContactToast && (
                    <div className="mt-2 text-[11px] font-semibold text-emerald-600 flex items-center gap-1">
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {carrierContactToast}
                    </div>
                  )}
                </div>
              )}

              {/* Payer denial info if exists */}
              {selectedAppeal.payer_denial_reason && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                  <p className="text-xs font-bold text-red-400 uppercase mb-1">Payer's Denial Reason</p>
                  <p className="text-sm text-red-700">{selectedAppeal.payer_denial_reason}</p>
                  {selectedAppeal.payer_denial_code && (
                    <p className="text-xs text-red-500 mt-1">Code: {selectedAppeal.payer_denial_code}</p>
                  )}
                </div>
              )}

              {/* Feedback / learning */}
              {selectedAppeal.feedback && selectedAppeal.feedback.length > 0 && (
                <div className="bg-violet-50 border border-violet-200 rounded-xl p-4">
                  <p className="text-xs font-bold text-violet-400 uppercase mb-2">Learning from This Denial</p>
                  {selectedAppeal.feedback.map(f => (
                    <div key={f.id} className="space-y-2">
                      {f.lesson_learned && <p className="text-sm text-violet-700">{f.lesson_learned}</p>}
                      {f.should_include_next_time && (
                        <div className="bg-white/60 rounded-lg p-2 mt-2">
                          <p className="text-xs font-semibold text-violet-600">Improvements for Next Time:</p>
                          <p className="text-xs text-violet-600 mt-0.5">{f.should_include_next_time}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Letter preview */}
              <div>
                <p className="text-xs font-bold text-slate-400 uppercase mb-2">Generated Letter</p>
                <div className="bg-slate-50 rounded-xl p-4 max-h-60 overflow-auto">
                  <pre className="text-xs text-slate-600 whitespace-pre-wrap font-sans leading-relaxed">{selectedAppeal.letter_text}</pre>
                </div>
              </div>

              {/* Created date */}
              <p className="text-xs text-slate-400">
                Generated {new Date(selectedAppeal.created_at).toLocaleString()} &bull; Mode: {selectedAppeal.generation_mode}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ── Advance Appeal Modal ── */}
      {showAdvanceModal && selectedAppeal && (
        <div className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm flex items-center justify-center">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-cyan-600 px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-white font-bold text-lg">Record Stage Result</h2>
                <p className="text-blue-200 text-xs">
                  {timelineData?.current_stage_label || 'Current stage'} for {selectedAppeal.denied_product}
                </p>
              </div>
              <button onClick={() => dispatchUI({ type: 'SET_SHOW_ADVANCE_MODAL', value: false })} className="text-white/70 hover:text-white">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-slate-600">
                What was the result at this appeal stage?
              </p>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Notes (optional)</label>
                <textarea
                  value={advanceNotes}
                  onChange={e => dispatchForms({ type: 'SET_ADVANCE_NOTES', value: e.target.value })}
                  placeholder="e.g., Payer upheld denial citing insufficient step therapy documentation..."
                  rows={3}
                  className="w-full border border-slate-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
                <p className="text-[11px] text-amber-600 mt-1">&#9888; Do not enter patient-identifiable information in notes. Use clinical descriptions only.</p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => handleAdvanceAppeal('approved')}
                  disabled={advancingAppeal}
                  className="flex-1 px-4 py-2.5 bg-emerald-600 text-white text-sm font-semibold rounded-xl hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {advancingAppeal ? (
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                  )}
                  Approved
                </button>
                <button
                  onClick={() => handleAdvanceAppeal('denied')}
                  disabled={advancingAppeal}
                  className="flex-1 px-4 py-2.5 bg-red-600 text-white text-sm font-semibold rounded-xl hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {advancingAppeal ? (
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                  )}
                  Denied
                </button>
              </div>
              <p className="text-[10px] text-slate-400 text-center">
                If denied, the appeal will automatically escalate to the next level.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ── Denial Feedback Modal ── */}
      {showFeedbackForm && (
        <div className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm flex items-center justify-center">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
            <div className="bg-gradient-to-r from-violet-600 to-indigo-600 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
                </svg>
                <div>
                  <h2 className="text-white font-bold text-lg">Report Denial &amp; Teach RxCheckUp</h2>
                  <p className="text-violet-200 text-xs">This feedback improves future LMNs for similar cases</p>
                </div>
              </div>
              <button onClick={() => dispatchUI({ type: 'SET_SHOW_FEEDBACK_FORM', value: false })} className="text-white/70 hover:text-white">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            {feedbackResult ? (
              <div className="p-6 space-y-4">
                <div className="text-center mb-4">
                  <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
                    <svg className="w-7 h-7 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="font-bold text-slate-800 text-lg">Feedback Recorded!</h3>
                  <p className="text-sm text-slate-500">RxCheckUp has learned from this denial.</p>
                </div>

                <div className="bg-violet-50 border border-violet-200 rounded-xl p-4">
                  <p className="text-xs font-bold text-violet-400 uppercase mb-1">What RxCheckUp Learned</p>
                  <p className="text-sm text-violet-700">{feedbackResult.lesson}</p>
                </div>

                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                  <p className="text-xs font-bold text-emerald-400 uppercase mb-1">Improvements for Next Time</p>
                  <p className="text-sm text-emerald-700">{feedbackResult.improvements}</p>
                </div>

                <p className="text-xs text-slate-400 text-center">
                  Next time you generate an LMN for this carrier or drug, these insights will be applied automatically.
                </p>

                <button onClick={() => { dispatchUI({ type: 'SET_SHOW_FEEDBACK_FORM', value: false }); dispatchForms({ type: 'SET_FEEDBACK_RESULT', value: null }) }}
                  className="w-full py-2.5 bg-violet-600 text-white text-sm font-semibold rounded-xl hover:bg-violet-700">
                  Close
                </button>
              </div>
            ) : (
              <div className="p-6 space-y-4">
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                  <p className="text-xs text-amber-700">
                    <span className="font-bold">HIPAA Safe:</span> Only describe the payer's requirements — no patient names,
                    dates of birth, or medical record numbers. This data stays in your account only.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                    Why did the payer deny the appeal? *
                  </label>
                  <textarea value={feedbackPayerReason} onChange={e => dispatchForms({ type: 'SET_FEEDBACK_PAYER_REASON', value: e.target.value })}
                    placeholder="e.g., Payer stated the patient has not completed required step therapy with methotrexate..."
                    rows={3}
                    className="w-full border border-slate-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none" />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                    What does the payer require?
                  </label>
                  <textarea value={feedbackRequirements} onChange={e => dispatchForms({ type: 'SET_FEEDBACK_REQUIREMENTS', value: e.target.value })}
                    placeholder="e.g., Documentation of 3 months on methotrexate with lab values showing inadequate response..."
                    rows={2}
                    className="w-full border border-slate-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none" />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                    What documentation was missing?
                  </label>
                  <textarea value={feedbackMissingDocs} onChange={e => dispatchForms({ type: 'SET_FEEDBACK_MISSING_DOCS', value: e.target.value })}
                    placeholder="e.g., Lab results, specialist referral letter, prior medication history..."
                    rows={2}
                    className="w-full border border-slate-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none" />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                    Denial Code (if provided)
                  </label>
                  <input type="text" value={feedbackDenialCode} onChange={e => dispatchForms({ type: 'SET_FEEDBACK_DENIAL_CODE', value: e.target.value })}
                    placeholder="e.g., PA-ST-001"
                    className="w-full border border-slate-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent" />
                </div>

                <div className="flex gap-3 pt-2">
                  <button onClick={() => dispatchUI({ type: 'SET_SHOW_FEEDBACK_FORM', value: false })}
                    className="flex-1 px-4 py-2.5 border border-slate-300 text-slate-600 text-sm font-semibold rounded-xl hover:bg-slate-50">
                    Cancel
                  </button>
                  <button onClick={handleSubmitFeedback}
                    disabled={feedbackSubmitting || !feedbackPayerReason.trim()}
                    className="flex-1 px-4 py-2.5 bg-violet-600 text-white text-sm font-semibold rounded-xl hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                    {feedbackSubmitting ? (
                      <>
                        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Analyzing...
                      </>
                    ) : 'Submit & Teach RxCheckUp'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── P2P Prep Sheet Modal ── */}
      {showP2pModal && (
        <div className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm flex items-center justify-center">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl mx-4 max-h-[90vh] flex flex-col overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-orange-500 to-amber-500 px-6 py-4 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-3">
                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
                </svg>
                <div>
                  <h2 className="text-white font-bold text-lg">Peer-to-Peer Review Prep Sheet</h2>
                  {p2pData && (
                    <p className="text-orange-100 text-xs">{p2pData.metadata.drug} | {p2pData.metadata.carrier} | {p2pData.metadata.date}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {p2pData && (
                  <>
                    <button onClick={handlePrintP2P}
                      className="px-3 py-1.5 bg-white/20 text-white text-xs font-semibold rounded-lg hover:bg-white/30">
                      Print
                    </button>
                    <button onClick={handleDownloadP2PPDF}
                      className="px-3 py-1.5 bg-white/20 text-white text-xs font-semibold rounded-lg hover:bg-white/30">
                      Download
                    </button>
                  </>
                )}
                <button onClick={() => dispatchUI({ type: 'SET_SHOW_P2P_MODAL', value: false })} className="text-white/80 hover:text-white ml-2">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="overflow-auto flex-1 p-6">
              {p2pLoading && (
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="w-10 h-10 border-3 border-orange-500 border-t-transparent rounded-full animate-spin mb-4" />
                  <p className="text-slate-500 text-sm font-medium">Generating your P2P prep sheet...</p>
                  <p className="text-slate-400 text-xs mt-1">Building talking points and anticipated objections</p>
                </div>
              )}

              {p2pData && !p2pLoading && (
                <div className="space-y-3">
                  {/* Opening Statement */}
                  <div className="border border-emerald-200 rounded-xl overflow-hidden">
                    <button onClick={() => toggleP2pSection('opening')}
                      className="w-full flex items-center justify-between px-4 py-3 bg-emerald-50 hover:bg-emerald-100 transition-colors text-left">
                      <span className="text-sm font-bold text-emerald-700 uppercase tracking-wide">Your Opening Statement (30 sec)</span>
                      <svg className={`w-4 h-4 text-emerald-500 transition-transform ${p2pExpandedSections.opening ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {p2pExpandedSections.opening && (
                      <div className="px-4 py-3 bg-white">
                        <p className="text-sm text-emerald-800 italic leading-relaxed">"{p2pData.opening_statement}"</p>
                      </div>
                    )}
                  </div>

                  {/* Talking Points */}
                  <div className="border border-teal-200 rounded-xl overflow-hidden">
                    <button onClick={() => toggleP2pSection('talking_points')}
                      className="w-full flex items-center justify-between px-4 py-3 bg-teal-50 hover:bg-teal-100 transition-colors text-left">
                      <span className="text-sm font-bold text-teal-700 uppercase tracking-wide">Key Talking Points ({p2pData.talking_points.length})</span>
                      <svg className={`w-4 h-4 text-teal-500 transition-transform ${p2pExpandedSections.talking_points ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {p2pExpandedSections.talking_points && (
                      <div className="px-4 py-3 bg-white space-y-3">
                        {p2pData.talking_points.map((tp, i) => (
                          <div key={i} className="border-l-3 border-teal-400 pl-3">
                            <p className="text-sm font-bold text-teal-700">{i + 1}. {tp.point}</p>
                            <p className="text-xs text-slate-600 mt-1 leading-relaxed">{tp.detail}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Objections & Responses */}
                  <div className="border border-red-200 rounded-xl overflow-hidden">
                    <button onClick={() => toggleP2pSection('objections')}
                      className="w-full flex items-center justify-between px-4 py-3 bg-red-50 hover:bg-red-100 transition-colors text-left">
                      <span className="text-sm font-bold text-red-700 uppercase tracking-wide">Anticipated Objections &amp; Responses ({p2pData.objections_responses.length})</span>
                      <svg className={`w-4 h-4 text-red-500 transition-transform ${p2pExpandedSections.objections ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {p2pExpandedSections.objections && (
                      <div className="px-4 py-3 bg-white space-y-3">
                        {p2pData.objections_responses.map((obj, i) => (
                          <div key={i} className="rounded-lg border border-slate-200 overflow-hidden">
                            <div className="bg-red-50 px-3 py-2">
                              <p className="text-xs"><span className="font-bold text-red-600">IF THEY SAY:</span> <span className="text-red-800">"{obj.objection}"</span></p>
                            </div>
                            <div className="bg-emerald-50 px-3 py-2">
                              <p className="text-xs"><span className="font-bold text-emerald-600">RESPOND WITH:</span> <span className="text-emerald-800">"{obj.response}"</span></p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Clinical Data */}
                  <div className="border border-blue-200 rounded-xl overflow-hidden">
                    <button onClick={() => toggleP2pSection('clinical')}
                      className="w-full flex items-center justify-between px-4 py-3 bg-blue-50 hover:bg-blue-100 transition-colors text-left">
                      <span className="text-sm font-bold text-blue-700 uppercase tracking-wide">Clinical Data to Reference</span>
                      <svg className={`w-4 h-4 text-blue-500 transition-transform ${p2pExpandedSections.clinical ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {p2pExpandedSections.clinical && (
                      <div className="px-4 py-3 bg-white space-y-2">
                        <p className="text-xs"><span className="font-semibold text-blue-700">FDA Approval:</span> <span className="text-slate-600">{p2pData.clinical_data.fda_approval}</span></p>
                        {p2pData.clinical_data.key_trial && (
                          <p className="text-xs"><span className="font-semibold text-blue-700">Key Trial:</span> <span className="text-slate-600">{p2pData.clinical_data.key_trial}</span></p>
                        )}
                        {p2pData.clinical_data.guidelines.map((g, i) => (
                          <p key={i} className="text-xs"><span className="font-semibold text-blue-700">Guideline:</span> <span className="text-slate-600">{g}</span></p>
                        ))}
                        <p className="text-xs bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-2">
                          <span className="font-semibold text-amber-700">Patient Metrics:</span> <span className="text-amber-600">{p2pData.clinical_data.patient_metrics}</span>
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Carrier Tips */}
                  <div className="border border-amber-200 rounded-xl overflow-hidden">
                    <button onClick={() => toggleP2pSection('carrier_tips')}
                      className="w-full flex items-center justify-between px-4 py-3 bg-amber-50 hover:bg-amber-100 transition-colors text-left">
                      <span className="text-sm font-bold text-amber-700 uppercase tracking-wide">Carrier-Specific Tips</span>
                      <svg className={`w-4 h-4 text-amber-500 transition-transform ${p2pExpandedSections.carrier_tips ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {p2pExpandedSections.carrier_tips && (
                      <div className="px-4 py-3 bg-white">
                        <ul className="space-y-2">
                          {p2pData.carrier_tips.map((tip, i) => (
                            <li key={i} className="text-xs text-amber-800 leading-relaxed flex items-start gap-2">
                              <span className="text-amber-500 mt-0.5 font-bold flex-shrink-0">&bull;</span>
                              <span>{tip}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Closing Statement */}
                  <div className="border border-emerald-200 rounded-xl overflow-hidden">
                    <button onClick={() => toggleP2pSection('closing')}
                      className="w-full flex items-center justify-between px-4 py-3 bg-emerald-50 hover:bg-emerald-100 transition-colors text-left">
                      <span className="text-sm font-bold text-emerald-700 uppercase tracking-wide">Closing Statement</span>
                      <svg className={`w-4 h-4 text-emerald-500 transition-transform ${p2pExpandedSections.closing ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {p2pExpandedSections.closing && (
                      <div className="px-4 py-3 bg-white">
                        <p className="text-sm text-emerald-800 italic leading-relaxed">"{p2pData.closing_statement}"</p>
                      </div>
                    )}
                  </div>

                  {/* If Unsuccessful */}
                  <div className="border-2 border-orange-300 rounded-xl overflow-hidden">
                    <button onClick={() => toggleP2pSection('unsuccessful')}
                      className="w-full flex items-center justify-between px-4 py-3 bg-orange-50 hover:bg-orange-100 transition-colors text-left">
                      <span className="text-sm font-bold text-orange-700 uppercase tracking-wide">If the Call Doesn't Go Well</span>
                      <svg className={`w-4 h-4 text-orange-500 transition-transform ${p2pExpandedSections.unsuccessful ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {p2pExpandedSections.unsuccessful && (
                      <div className="px-4 py-3 bg-white">
                        <ul className="space-y-2">
                          {p2pData.if_unsuccessful.map((item, i) => (
                            <li key={i} className="text-xs text-orange-800 leading-relaxed flex items-start gap-2">
                              <span className="text-orange-500 mt-0.5 font-bold flex-shrink-0">{i + 1}.</span>
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}


// ── Stat Card Component ────────────────────────────────────────────────────

function StatCard({ label, value, color }: { label: string; value: number; color: string }) {
  const colorClasses: Record<string, string> = {
    slate: 'text-slate-800',
    blue: 'text-blue-600',
    emerald: 'text-emerald-600',
    red: 'text-red-600',
  }
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">{label}</p>
      <p className={`text-2xl font-extrabold mt-1 ${colorClasses[color] || 'text-slate-800'}`}>{value}</p>
    </div>
  )
}
